import { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccountModules, useModuleAccess } from '@/hooks/useAccountModules';
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  CreditCard,
  DollarSign,
  ExternalLink,
  Gift,
  Package,
  Printer,
  Plus,
  Search,
  Star,
  Trash2,
  Undo2,
  Users,
} from 'lucide-react';
import { usePageContext } from '@/hooks/usePageContext';
import { MY_TASK_COUNTS_KEY } from '@/hooks/useMyTaskCounts';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PERMISSION } from '@/lib/permissions';
import { useAISidebarStore } from '@/store/aiSidebarStore';
import { DetailPageLayout } from '@/components/layout/DetailPageLayout';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { DetailSubNav } from '@/components/layout/DetailSubNav';
import { Avatar, Badge, Button, Spinner } from '@/components/ui';
import { Switch } from '@/components/ui/Switch';
import { FilterSelect, type FilterSelectOption } from '@/components/ui/FilterSelect';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import {
  appointmentsService,
  getAppointmentsByPatient,
  type Appointment,
  type AppointmentStatus,
} from '@/services/appointments.service';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import {
  type ProductTypesResponse,
} from '@/components/patients/PatientProductsDisplay';
import {
  buildApptProductStats,
  getAppointmentActionReasons,
  type ApptProductStats,
} from '@/lib/appointmentActions';
import { formatApptDateTimeInFacilityTz, toLocaleDateStringInTz } from '@/lib/facilityTimezone';
import { formatPaymentDate, paymentTypeLabel } from '@/pages/payments/paymentsShared';
import {
  deleteBillingMethod,
  getPaymentStats,
  getPayments,
  listBillingMethods,
  listPatientGiftCards,
  listPaymentTerminals,
  restoreBillingMethod,
  setDefaultBillingMethod,
  type BillingMethod,
  type GiftCard,
  type Payment,
  type PaymentTerminal,
} from '@/services/payments.service';
import { listTasks, completeTask, deactivateTask, getTaskCounts, getTaskFilterTab, saveTaskFilterTab, type Task, type TaskSource } from '@/services/tasks.service';
import { TaskDoneButton } from '@/components/tasks/TaskDoneButton';
import { parseDateValue } from '@/lib/utils';
import { DirectPaymentLinkModal } from '@/pages/payments/DirectPaymentLinkModal';
import { invalidatePatientAppointments, usePatientDetail } from '@/hooks/usePatientDetail';
import { usePatientPhoto } from '@/hooks/usePatientPhoto';
import { AppointmentCreateModal } from '@/pages/appointments/AppointmentCreateModal';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import { CompleteModal, TaskModal } from '@/pages/TasksPage';
import { isTaskOpen, planNumberOrNull, taskStatusLabel as resolveTaskStatusLabel } from '@/pages/tasks/tasksShared';
import { HealthHistoryTab } from '@/pages/patients/HealthHistoryTab';
import { PatientInfoDemographicsTab } from '@/pages/patients/PatientInfoDemographicsTab';
import { PlansTab } from '@/pages/patients/PlansTab';
import { SurgeryTab } from '@/pages/patients/SurgeryTab';
import { getPlansByPatient } from '@/services/plans.service';
import { getErrorMessage } from '@/services/apiClient';
import PatientPackagesPanel from '@/pages/patients/PatientPackagesPanel';
import PatientQuotesPanel from '@/pages/quotes/PatientQuotesPanel';
import DocumentsTabFull from '@/pages/patients/DocumentsTab';
import { PatientMembershipsPanel } from '@/pages/memberships/PatientMembershipsPanel';
import { ChargePaymentModal } from '@/pages/payments/ChargePaymentModal';
import { RecordPatientPaymentModal } from '@/pages/payments/RecordPatientPaymentModal';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import { TerminalCheckoutPanel, type TerminalFlowResult } from '@/components/payments/TerminalCheckoutPanel';
import GiftCardDetailModal from '@/pages/gift-cards/GiftCardDetailModal';
import IssueGiftCardModal from '@/pages/gift-cards/IssueGiftCardModal';
import { FinancingTab } from '@/pages/patients/FinancingTab';
import { LabsTab } from '@/pages/patients/LabsTab';
import { BodySpecScansSection } from '@/pages/patients/BodySpecScansSection';
import { LabCategoryDocumentsTab } from '@/pages/patients/labs/LabCategoryDocumentsTab';
import { getPatientImagingDocuments, getPatientGeneticsDocuments } from '@/services/labs.service';
import { LabPdfPreviewPanel, type LabPreviewTarget } from '@/pages/patients/labs/LabPdfPreviewPanel';
import { LabDetailPanel, type LabDetailTarget } from '@/pages/patients/labs/LabDetailPanel';
import { QuestLabDetailModal } from '@/pages/patients/labs/QuestLabDetailModal';
import { VibrantLabDetailModal } from '@/pages/patients/labs/VibrantLabDetailModal';
import { AccessLabDetailModal } from '@/pages/patients/labs/AccessLabDetailModal';
import { PartellTab } from '@/pages/patients/PartellTab';
import { PeptidesTab } from '@/pages/patients/PeptidesTab';
import { ORDER_PARAM, ORDER_VENDOR_PARAM, parseLinkedOrder } from '@/pages/patients/labs/linkedOrder';
import { getVitaboomEnabled } from '@/services/vitaboom.service';
// Lazy-loaded: the Vitaboom SDK pulls in MUI + Emotion (~a few hundred KB), so
// code-split it into its own chunk that loads only when the Vitamins tab opens
// rather than taxing every patient page.
const VitaminsTab = lazyWithRetry(
  () => import('@/pages/patients/VitaminsTab').then((m) => ({ default: m.VitaminsTab })),
  'vitamins-tab',
);
import { MedicationsTab } from '@/pages/patients/MedicationsTab';
import { PrescriptionsTab } from '@/pages/patients/PrescriptionsTab';
import { QaTab } from '@/pages/patients/QaTab';
import { PostOpsTab } from '@/pages/patients/PostOpsTab';
import { ItemsTab } from '@/pages/patients/ItemsTab';
import { PatientFilesTab } from '@/pages/patients/PatientFilesTab';
import { ReferralsTab } from '@/pages/patients/ReferralsTab';
import { HealthNexusSection } from '@/pages/patients/HealthNexusSection';
import { FormsTab } from '@/pages/patients/FormsTab';
import { GiFormSendModal } from '@/pages/patients/GiFormSendModal';
import { InvestigateFormsModal } from '@/pages/patients/InvestigateFormsModal';
import { listPatientForms } from '@/services/patientForms.service';
import type { PatientSummary } from '@/types/patient.types';
import { CommLogSectionWithBoundary as CommLogSection } from '@/v5/components/CommLogSection';
import { NotesSection } from '@/v5/components/NotesSection';
import { useVideoCall } from '@/v5/hooks/useVideoCall';
import { useVoiceCall } from '@/v5/hooks/useVoiceCall';
import { listUsers, type AdminUser } from '@/services/admin.service';
import { adminApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import {
  DEFAULT_SECTION,
  HEALTH_NEXUS_MODULE_KEY,
  POST_OPS_MODULE_KEY,
  PATIENTS_MODULE_KEY,
  QUOTES_MODULE_KEY,
  SUB_SECTIONS,
  SURGERY_MODULE_KEY,
  computeVisibleSections,
  resolveEffectiveSection,
} from './patientDetailSections';

// Sections that share the lab PDF-preview right panel (Labs + its category
// siblings Imaging/Genetics, which reuse handleLabPreview / labPreview).
function isLabLikeSection(section: string): boolean {
  return section === 'labs' || section === 'imaging' || section === 'genetics';
}

function resolveInitialPatientSection(sectionParam: string | null): string {
  if (sectionParam && SUB_SECTIONS.some((s) => s.key === sectionParam)) {
    return sectionParam;
  }
  return DEFAULT_SECTION;
}

// p-6 spends 48px of a 375px viewport, so the phone tier gets p-4 back.
// overflow-x-hidden is a backstop, not a fix: `overflow-y-auto` computes
// overflow-x to `auto`, so any one over-wide descendant turns this box into a
// sideways scroller. The descendants are meant to fit; this keeps a future
// miss from reintroducing the horizontal scroll. Both are max-md:, so every
// section of the chart is unchanged at md and above.
const SECTION_SHELL_CLASS =
  'h-full min-h-0 overflow-y-auto bg-white p-6 max-md:p-4 max-md:overflow-x-hidden';

function PlaceholderSection({
  sectionName,
  route,
}: {
  sectionName: string;
  route?: string;
}) {
  return (
    <div className="flex h-full items-center justify-center bg-white">
      <div className="max-w-sm space-y-2 text-center">
        <h2 className="text-lg font-semibold text-slate-900">{sectionName}</h2>
        <p className="text-sm text-slate-500">
          This section does not yet have a patient-scoped implementation in the V5 shell.
        </p>
        {route ? (
          <div className="pt-2">
            <Link
              to={route}
              className="inline-flex h-8 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Open section page
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SectionTitle({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    // `max-md:flex-wrap` lets the action group drop to its own line rather than forcing
    // the row wider than the viewport. The action node arrives already constructed, so
    // this cannot give it props — each call site scopes its own group (SCRUM-1405).
    <div className="flex items-center justify-between gap-4 max-md:flex-wrap">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {action ?? null}
    </div>
  );
}

function formatShortDate(value: string | null | undefined, tz?: string): string {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return toLocaleDateStringInTz(parsed, 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  });
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}


// ─── Appointments Tab ─────────────────────────────────────────────────────────

const APPT_STAT_TONES = {
  slate: 'text-slate-900',
  red: 'text-red-600',
  amber: 'text-amber-600',
} as const;

// Legacy parity: generate_printable_appointments.php prints one row per
// appointment with Type / Resources / Status / Duration / Facility / Date.
// Same column set on the print sheet, and the same labels name the raw
// statuses in the status filter. Module scope so the filter's useMemo can read
// them — it runs before the body of AppointmentsTab reaches this far.
const APPT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Checked Out',
  no_show: 'No Show',
  cancelled: 'Cancelled',
  contact_attempted: 'Contact Attempted',
  waiting_list: 'Waiting List',
  reschedule_pending: 'Reschedule Pending',
  checked_in: 'Checked In',
  checked_back: 'Checked Back',
  action_required: 'Action Required',
};

// Dot colours for the status filter. Saturated siblings of the `statusColors`
// badge classes the cards already use, so a status reads the same in the
// dropdown as it does on the card.
const APPT_STATUS_DOT: Record<string, string> = {
  scheduled: '#3b82f6',
  confirmed: '#8b5cf6',
  completed: '#22c55e',
  no_show: '#ef4444',
  cancelled: '#94a3b8',
  contact_attempted: '#f59e0b',
  waiting_list: '#06b6d4',
  reschedule_pending: '#f97316',
  checked_in: '#0ea5e9',
  checked_back: '#14b8a6',
  action_required: '#f59e0b',
};

/**
 * The status filter's four bucket values — one per header stat tile, so
 * clicking a tile and picking the matching entry in the dropdown select the
 * exact same rows. Buckets are deliberately NOT raw statuses: "No-Shows" reads
 * the `noShow` flag, "Actions Required" is computed from product stats, and
 * "Appointments" is the leftover partition. Raw statuses are offered too, under
 * the `status:` prefix, which is what keeps the two namespaces from colliding.
 */
const APPT_BUCKET = {
  appointments: 'bucket:appointments',
  noShow: 'bucket:no_show',
  cancelled: 'bucket:cancelled',
  actionRequired: 'bucket:action_required',
} as const;

/**
 * A header stat tile. Every tile is also the control for the status filter
 * bucket it counts: clicking one selects that bucket, clicking the selected one
 * again clears it. `active` is what tells the user which bucket the lists below
 * are currently narrowed to — the dropdown says the same thing, they are two
 * views of one piece of state.
 */
function ApptStatTile({
  label,
  value,
  hint,
  tone = 'slate',
  onClick,
  active = false,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: keyof typeof APPT_STAT_TONES;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hint}
      className={`rounded-xl border px-4 py-2 text-left transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        active
          ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
          : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
      }`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-lg font-semibold leading-tight ${APPT_STAT_TONES[tone]}`}>{value}</p>
    </button>
  );
}

function AppointmentsTab({
  patientId,
  patientSummary,
}: {
  patientId: number;
  patientSummary: PatientSummary | null;
}) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [loadingAppointmentId, setLoadingAppointmentId] = useState<number | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const suppressCloseRef = useRef(false);

  // Deleted appointments are audit-ended rows the list normally hides entirely.
  // They're diagnostic ("where did that appointment go?"), so level 5 only.
  const { hasMinUserLevel } = useAuth();
  const canSeeDeleted = hasMinUserLevel(5);
  const [showDeleted, setShowDeleted] = useState(false);
  const includeDeleted = canSeeDeleted && showDeleted;

  const { data: appointments, isLoading, isError } = useQuery({
    queryKey: ['patient-appointments', patientId, includeDeleted],
    queryFn: () => getAppointmentsByPatient(patientId, includeDeleted),
    staleTime: 30_000,
  });

  const { data: productData } = useQuery({
    queryKey: ['patient-appointments-products', patientId],
    queryFn: () => getPatientProductTypes(String(patientId)),
    staleTime: 60_000,
  });

  // The appointments payload carries `appointmentTypeGroupId` but no group NAME
  // (AppointmentPresenterService L214), so searching by group means resolving it
  // here. Same query key / 5-minute cache the scheduler filters already use, so
  // this is usually a cache hit. `/appointment-types` returns active types only;
  // a group survives as long as one active type still sits in it.
  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60_000,
  });

  const groupNameByGroupId = useMemo(() => {
    const map = new Map<number, string>();
    for (const type of appointmentTypes ?? []) {
      const groupId = type.appointmentTypeGroupId;
      if (groupId == null || !type.appointmentTypeGroupName) continue;
      if (!map.has(groupId)) map.set(groupId, type.appointmentTypeGroupName);
    }
    return map;
  }, [appointmentTypes]);

  const apptStatsMap = useMemo(
    () => buildApptProductStats(productData as ProductTypesResponse | undefined),
    [productData],
  );

  const now = new Date();
  const todayStr = now.toISOString();
  // Everything the endpoint returned, before the search / facility filters.
  // The header stat tiles read this so they stay whole-patient totals that
  // reconcile with the left rail; only the lists below follow the filters.
  const allRows = appointments ?? [];

  // Header tiles. Cancelled / no-show read the same `status` and `noShow` the
  // cards below render, so the counts can never disagree with the badges.
  //
  // Scoped to live rows so toggling "Show deleted" adds diagnostic rows to the
  // list without moving these numbers — deletedCount reports those separately.
  // Read `allRows`, not the filtered `rows`, for the same reason: these are
  // whole-patient totals that have to keep reconciling with the left rail, so
  // searching or filtering narrows the lists but never the tiles.
  // The "showing N of M" line under the filters covers the filtered count.
  //
  // Total = Appointments + No-Shows + Cancelled, a clean partition; that's why
  // noShowCount excludes cancelled rows instead of counting them independently.
  // "Actions Required" is a SUBSET of Appointments (it already drops cancelled
  // and no-show), so it deliberately does not add into Total.
  //
  // The left-rail "Appointments" stat counts a fourth way: `appointmentCount`
  // from /patients/{id}/sidebar-stats excludes cancelled, so it should equal
  // Appointments + No-Shows here — not Total.
  //
  // These sit above the filters because each tile is also the control for the
  // status bucket it counts, and the dropdown shows the identical numbers.
  const liveRows = allRows.filter((a) => !a.deleted);
  const cancelledCount = liveRows.filter((a) => a.status === 'cancelled').length;
  const noShowCount = liveRows.filter((a) => a.noShow && a.status !== 'cancelled').length;
  const actualCount = liveRows.length - cancelledCount - noShowCount;

  // Tile counterpart to `actionRequired` below, over every row instead of the
  // filtered ones, so all five tiles stay whole-patient totals together. Kept
  // as an id set rather than a bare count because the "Actions Required" status
  // bucket has to reselect exactly these rows.
  const actionRequiredIdsAll = new Set(
    allRows
      .filter((a) => a.startTime < todayStr && apptActionReasons(a).length > 0)
      .map((a) => a.appointmentId),
  );
  const actionRequiredTotal = actionRequiredIdsAll.size;

  const [search, setSearch] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // One option per facility the patient has actually been seen at — a dropdown
  // of every facility on the account would be mostly dead entries here.
  const facilityOptions = useMemo<FilterSelectOption[]>(() => {
    const byFacility = new Map<string, { label: string; count: number }>();
    for (const a of allRows) {
      const key = a.facilityId == null ? 'none' : String(a.facilityId);
      const existing = byFacility.get(key);
      if (existing) existing.count += 1;
      else byFacility.set(key, { label: a.facilityName?.trim() || 'No facility', count: 1 });
    }
    return [
      { value: '', label: 'All facilities', count: allRows.length },
      ...[...byFacility.entries()]
        .map(([value, { label, count }]) => ({ value, label, count }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [allRows]);

  // Bucket entries first — one per stat tile, carrying the tile's own number —
  // then whatever raw statuses the patient's rows actually use, same
  // seen-here-only rule as the facility list. Buckets are always offered even at
  // zero so the dropdown and the tile row stay a mirror of each other.
  //
  // Plain const rather than a useMemo: every input is a per-render array, so a
  // memo here would recompute on every render anyway and only add a dependency
  // list to keep honest.
  const statusOptions: FilterSelectOption[] = (() => {
    const byStatus = new Map<string, number>();
    for (const a of liveRows) byStatus.set(a.status, (byStatus.get(a.status) ?? 0) + 1);
    // The buckets already own these three, and they count them more precisely
    // than the raw status does (no-show is a flag, not a status). Offering both
    // would put two entries with the same name and different numbers in one list.
    for (const covered of ['cancelled', 'no_show', 'action_required']) byStatus.delete(covered);
    return [
      { value: '', label: 'All statuses', count: allRows.length },
      { value: APPT_BUCKET.appointments, label: 'Appointments', count: actualCount, color: APPT_STATUS_DOT.scheduled },
      { value: APPT_BUCKET.noShow, label: 'No-Shows', count: noShowCount, color: APPT_STATUS_DOT.no_show },
      { value: APPT_BUCKET.cancelled, label: 'Cancelled', count: cancelledCount, color: APPT_STATUS_DOT.cancelled },
      { value: APPT_BUCKET.actionRequired, label: 'Actions Required', count: actionRequiredTotal, color: APPT_STATUS_DOT.action_required },
      ...[...byStatus.entries()]
        .map(([status, count]) => ({
          value: `status:${status}`,
          label: APPT_STATUS_LABELS[status as AppointmentStatus] ?? status,
          count,
          color: APPT_STATUS_DOT[status],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  })();

  // Deleted rows are outside every bucket for the same reason they are outside
  // every tile: they are audit rows a level-5 user opts into, not appointments
  // with a status. So any active status filter hides them, and "Show deleted"
  // only does anything while the filter is on "All statuses".
  function matchesStatusFilter(a: Appointment): boolean {
    if (statusFilter === '') return true;
    if (a.deleted) return false;
    switch (statusFilter) {
      case APPT_BUCKET.appointments:
        return a.status !== 'cancelled' && !a.noShow;
      case APPT_BUCKET.noShow:
        return a.noShow && a.status !== 'cancelled';
      case APPT_BUCKET.cancelled:
        return a.status === 'cancelled';
      case APPT_BUCKET.actionRequired:
        return actionRequiredIdsAll.has(a.appointmentId);
      default:
        return `status:${a.status}` === statusFilter;
    }
  }

  // Clicking the tile that is already selected clears it, so the tiles work as
  // a toggle row rather than a one-way trip into a filtered state.
  function toggleStatusFilter(value: string) {
    setStatusFilter((current) => (current === value ? '' : value));
  }

  // Every whitespace-separated token has to match, so "botox austin" narrows
  // instead of widening. Matched against group, type and title — the three
  // things a card actually shows for "what was this appointment".
  const searchTokens = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  // Status is applied in a second pass below, so it stays out of this one.
  const searchOrFacilityActive = searchTokens.length > 0 || facilityFilter !== '';
  const filtersActive = searchOrFacilityActive || statusFilter !== '';

  // Printed onto the sheet so a filtered print says what it was filtered by.
  const activeFilterSummary = [
    search.trim() ? `search "${search.trim()}"` : null,
    facilityFilter !== ''
      ? facilityOptions.find((o) => o.value === facilityFilter)?.label ?? null
      : null,
    statusFilter !== ''
      ? statusOptions.find((o) => o.value === statusFilter)?.label ?? null
      : null,
  ].filter(Boolean).join(', ');

  const searchedRows = useMemo(() => {
    if (!searchOrFacilityActive) return allRows;
    return allRows.filter((a) => {
      if (facilityFilter !== '') {
        const key = a.facilityId == null ? 'none' : String(a.facilityId);
        if (key !== facilityFilter) return false;
      }
      if (searchTokens.length === 0) return true;
      const groupName = a.appointmentTypeGroupId == null
        ? null
        : groupNameByGroupId.get(a.appointmentTypeGroupId) ?? null;
      const haystack = [groupName, a.appointmentTypeName, a.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchTokens.every((token) => haystack.includes(token));
    });
  }, [allRows, facilityFilter, groupNameByGroupId, searchOrFacilityActive, searchTokens]);

  // Second pass, deliberately outside the memo: the bucket predicate reads
  // per-render values (the action-required id set), and this list is one
  // patient's appointments, so the extra sweep costs nothing.
  const rows = statusFilter === '' ? searchedRows : searchedRows.filter(matchesStatusFilter);

  const upcoming = rows
    .filter((a) => a.startTime >= todayStr)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  const allPast = rows
    .filter((a) => a.startTime < todayStr)
    .sort((a, b) => b.startTime.localeCompare(a.startTime));

  const deletedCount = allRows.filter((a) => a.deleted).length;

  // Shared with the sidebar's Recent Visits cards — see lib/appointmentActions.
  // A cancelled / no-show / deleted appointment can't be checked out or
  // disbursed against, which that rule already accounts for.
  function apptActionReasons(a: Appointment): string[] {
    return getAppointmentActionReasons(
      {
        checkedOutAt: a.checkedOutAt,
        noShow: a.noShow,
        cancelled: a.status === 'cancelled',
        deleted: a.deleted,
      },
      apptStatsMap.get(a.appointmentId),
    );
  }

  const actionRequired = allPast
    .filter((a) => apptActionReasons(a).length > 0)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  const actionRequiredIds = new Set(actionRequired.map((a) => a.appointmentId));
  const past = allPast.filter((a) => !actionRequiredIds.has(a.appointmentId));

  const statusColors: Record<string, string> = {
    scheduled:  'bg-blue-100 text-blue-700',
    confirmed:  'bg-violet-100 text-violet-700',
    completed:  'bg-green-100 text-green-700',
    no_show:    'bg-red-100 text-red-700',
    cancelled:  'bg-slate-100 text-slate-500',
  };

  async function openAppointmentDetails(appointmentId: number) {
    setDetailError(null);
    setLoadingAppointmentId(appointmentId);
    try {
      const fullAppointment = await appointmentsService.getAppointment(appointmentId);
      setDetailAppointment(fullAppointment);
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : 'Failed to load appointment details.');
    } finally {
      setLoadingAppointmentId(null);
    }
  }

  async function refreshAppointments() {
    // Covers the tab lists here AND the sidebar Upcoming/Recent cards, which
    // read the same appointments under a different key
    // (patientQueryKeys.appointments) and used to stay stale until a reload.
    invalidatePatientAppointments(queryClient, patientId);

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['patient-product-types', String(patientId)] }),
      // The patient products/credits section (PatientProductsSection) is keyed
      // 'patient-demographics-products'; invalidate by prefix so it refreshes
      // after product changes such as a required-set swap.
      queryClient.invalidateQueries({ queryKey: ['patient-demographics-products'] }),
    ]);
  }

  function formatApptTime(isoStr: string, facilityTz: string | null): string {
    return formatApptDateTimeInFacilityTz(isoStr, facilityTz);
  }

  function handlePrintAppointments() {
    // Every appointment is already in memory (the endpoint isn't paginated), so
    // unlike the payments print there is nothing to re-fetch and the window can
    // be written synchronously.
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const esc = (value: unknown) =>
      String(value ?? '').replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
      );

    const displayName = patientSummary?.displayName ?? `Patient #${patientId}`;
    const heading = patientSummary?.displayId
      ? `${displayName} (#${patientSummary.displayId})`
      : displayName;
    // A DOB is a plain calendar date — parseDateValue keeps it off the UTC path.
    const dob = parseDateValue(patientSummary?.dateOfBirth);

    // Oldest first: a printed history reads forward, unlike the on-screen tab
    // which splits Upcoming from Past. `rows` follows the search / facility
    // filters and the "Show deleted" toggle, so the sheet matches whatever is
    // on screen — print a filtered list and you get that filtered list.
    const printRows = [...rows].sort((a, b) => a.startTime.localeCompare(b.startTime));

    const body = printRows
      .map((a) => {
        const type = a.name && a.appointmentTypeName
          ? `${a.name} (${a.appointmentTypeName})`
          : a.name ?? a.appointmentTypeName ?? '';
        const resources = a.resources.map((r) => r.resourceName).join(', ');
        const status = a.deleted
          ? 'Deleted'
          : APPT_STATUS_LABELS[a.status] ?? a.status;
        return `<tr${a.deleted ? ' class="deleted"' : ''}>
          <td>${esc(formatApptTime(a.startTime, a.facilityIanaTimezone))}</td>
          <td>${esc(type)}</td>
          <td>${esc(resources)}</td>
          <td>${esc(status)}</td>
          <td>${a.duration == null ? '' : `${a.duration} min`}</td>
          <td>${esc(a.facilityName)}</td>
        </tr>`;
      })
      .join('');

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Appointments – ${esc(heading)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #1e293b; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  .subtitle { font-size: 13px; color: #64748b; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; border-bottom: 2px solid #334155; padding: 6px 8px; font-weight: 600; }
  td { border-bottom: 1px solid #e2e8f0; padding: 5px 8px; }
  tr.deleted td { color: #94a3b8; text-decoration: line-through; }
  .empty { font-size: 13px; color: #64748b; padding: 12px 0; }
  @media print { body { margin: 0; } thead { display: table-header-group; } }
</style></head><body>
<h1>${esc(heading)}</h1>
<div class="subtitle">${dob ? `DOB: ${esc(dob.toLocaleDateString('en-US'))} &nbsp;·&nbsp; ` : ''}Appointment History – Printed ${new Date().toLocaleDateString('en-US')}</div>
${printRows.length === 0
      ? '<div class="empty">No appointments on file.</div>'
      : `<table>
  <thead><tr><th>Date</th><th>Type</th><th>Resources</th><th>Status</th><th>Duration</th><th>Facility</th></tr></thead>
  <tbody>${body}</tbody>
</table>
<div class="subtitle" style="margin-top:12px">${printRows.length} appointment${printRows.length === 1 ? '' : 's'}${includeDeleted ? ', including deleted' : ''}${filtersActive ? ` — filtered${activeFilterSummary ? ` (${esc(activeFilterSummary)})` : ''}, ${allRows.length} on file` : ''}.</div>`}
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  function AppointmentCard({ appt, stats, isPast, actionReasons }: {
    appt: Appointment;
    stats?: ApptProductStats;
    isPast?: boolean;
    actionReasons?: string[];
  }) {
    const isDeleted = Boolean(appt.deleted);
    // Deleted rows read as cancelled (the delete sets the cancel flag too), but
    // the deleted styling wins and they aren't openable — the detail endpoint
    // resolves to the live row of the audit chain and 404s on a deleted one.
    const isCancelled = appt.status === 'cancelled' || isDeleted;
    const borderColor = isCancelled ? '#94a3b8' : (appt.appointmentTypeColor ?? '#94a3b8');
    const statusClass = statusColors[appt.status] ?? 'bg-slate-100 text-slate-600';
    const isLoadingDetail = loadingAppointmentId === appt.appointmentId;
    const mutedText = isCancelled ? 'text-slate-400' : '';
    // Legacy's Resources column is a comma-joined list of resource names sorted
    // alphabetically (patient_appointment_list.php L147-160). The relation comes
    // back in pivot order, so sort here to match.
    const resourceNames = (appt.resources ?? [])
      .map((r) => r.resourceName?.trim())
      .filter((name): name is string => Boolean(name))
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
    const addonProducts = appt.addonProducts ?? [];

    return (
      <button
        type="button"
        onClick={() => {
          if (isDeleted) return;
          void openAppointmentDetails(appt.appointmentId);
        }}
        disabled={isLoadingDetail || isDeleted}
        title={isDeleted ? 'Deleted appointment — read only' : undefined}
        className={`flex w-full items-start gap-4 rounded-xl border p-4 text-left shadow-sm transition ${
          isDeleted
            ? 'border-dashed border-red-200 bg-red-50/40 disabled:cursor-default disabled:opacity-100'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow disabled:cursor-wait disabled:opacity-70'
        }`}
        style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}
      >
        <div className={`flex-1 min-w-0 space-y-1.5 ${mutedText}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${isCancelled ? 'text-slate-400' : 'text-slate-800'}`}>
              {formatApptTime(appt.startTime, appt.facilityIanaTimezone)}
            </span>
            {/* Legacy shows the stored duration in its own column as "N min."
                (patient_appointment_list.php L231). */}
            {appt.duration !== null && appt.duration > 0 && (
              <span className={`text-xs ${isCancelled ? 'text-slate-400' : 'text-slate-500'}`}>
                {appt.duration} min
              </span>
            )}
          </div>
          {appt.name && (
            <p className={`text-sm font-semibold ${isCancelled ? 'text-slate-400' : 'text-slate-800'}`}>
              {appt.name}
            </p>
          )}
          {(appt.appointmentTypeName || appt.firstAppointment || appt.firstVisit || appt.compAppointment) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Legacy renders the title bold with the type parenthesized and
                  italic beneath it; with no title the type is the headline. */}
              {appt.appointmentTypeName && (
                appt.name ? (
                  <span className={`text-sm italic ${isCancelled ? 'text-slate-400' : 'text-slate-500'}`}>({appt.appointmentTypeName})</span>
                ) : (
                  <span className={`text-sm font-semibold ${isCancelled ? 'text-slate-400' : 'text-slate-700'}`}>{appt.appointmentTypeName}</span>
                )
              )}
              {/* "First Time" is the manual first-of-this-type switch; "First Visit"
                  is derived server-side — the patient's first appointment ever. */}
              {appt.firstAppointment && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isCancelled ? 'bg-slate-100 text-slate-400' : 'bg-blue-100 text-blue-700'}`}>First Time</span>
              )}
              {appt.firstVisit && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isCancelled ? 'bg-slate-100 text-slate-400' : 'bg-violet-100 text-violet-700'}`}>First Visit</span>
              )}
              {appt.compAppointment && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isCancelled ? 'bg-slate-100 text-slate-400' : 'bg-amber-100 text-amber-700'}`}>COMP</span>
              )}
            </div>
          )}
          {isDeleted && appt.deletedAt && (
            <p className="text-xs font-medium text-red-600">
              Deleted {formatApptTime(appt.deletedAt, appt.facilityIanaTimezone)}
            </p>
          )}

          {stats && stats.totalRequired > 0 && (isPast || !stats.allAllocated || stats.purchaseRequired > 0) && (
            <div className="flex items-center gap-3 pt-0.5 flex-wrap">
              {(isPast || !stats.allAllocated) && (
                <span className="inline-flex items-center gap-1 text-xs">
                  {stats.allAllocated
                    ? <Check className={`h-3.5 w-3.5 ${isCancelled ? 'text-slate-400' : 'text-green-500'}`} />
                    : <AlertTriangle className={`h-3.5 w-3.5 ${isCancelled ? 'text-slate-400' : 'text-amber-500'}`} />}
                  <span className={isCancelled ? 'text-slate-400' : stats.allAllocated ? 'text-green-700' : 'text-amber-700'}>
                    {stats.totalAllocated}/{stats.totalRequired} allocated
                  </span>
                </span>
              )}
              {isPast && (
                <span className="inline-flex items-center gap-1 text-xs">
                  {stats.allDisbursed
                    ? <Check className={`h-3.5 w-3.5 ${isCancelled ? 'text-slate-400' : 'text-green-500'}`} />
                    : <AlertTriangle className={`h-3.5 w-3.5 ${isCancelled ? 'text-slate-400' : 'text-amber-500'}`} />}
                  <span className={isCancelled ? 'text-slate-400' : stats.allDisbursed ? 'text-green-700' : 'text-amber-700'}>
                    {stats.totalDisbursed}/{stats.totalRequired} disbursed
                  </span>
                </span>
              )}
              {stats.purchaseRequired > 0 && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isCancelled ? 'bg-slate-100 text-slate-400' : 'bg-green-50 text-green-700'}`}>
                  <DollarSign className="h-3 w-3" />
                  {stats.purchaseRequired} purchase req.
                </span>
              )}
            </div>
          )}

          {/* Add-ons only — the quantities the appointment type already
              requires are summarized by the allocated/disbursed stats above. */}
          {addonProducts.length > 0 && (
            <div className="flex items-center gap-1.5 pt-0.5 flex-wrap" title="Add-on products">
              <Package className="h-3 w-3 shrink-0 text-slate-400" />
              {addonProducts.map((product) => (
                <span
                  key={product.productTypeId}
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                    isCancelled
                      ? 'border-slate-200 bg-slate-100 text-slate-400'
                      : 'border-sky-200 bg-sky-50 text-sky-700'
                  }`}
                >
                  +{product.quantity} {product.productName}
                </span>
              ))}
            </div>
          )}

          {actionReasons && actionReasons.length > 0 && (
            <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
              {actionReasons.map((reason) => (
                <span key={reason} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                  {reason}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right rail: status, then the facility and resources that place the
            appointment. Capped so a long resource list can't crowd out the
            appointment details on the left. */}
        <div className="flex w-2/5 max-w-[16rem] shrink-0 flex-col items-end gap-1.5 text-right">
          <span className="flex items-center gap-1.5">
            {isDeleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                <Trash2 className="h-3 w-3" />
                Deleted
              </span>
            )}
            <span className={`rounded-full px-3 py-1 text-sm font-semibold capitalize ${statusClass}`}>
              {appt.status.replace('_', ' ')}
            </span>
          </span>
          {appt.facilityName && (
            <p className={`max-w-full truncate text-xs ${isCancelled ? 'text-slate-400' : 'text-slate-500'}`}>
              {appt.facilityName}
            </p>
          )}
          {resourceNames && (
            <p className={`flex max-w-full items-center justify-end gap-1.5 text-xs ${isCancelled ? 'text-slate-400' : 'text-slate-500'}`}>
              <Users className="h-3 w-3 shrink-0" />
              <span className="truncate">{resourceNames}</span>
            </p>
          )}
        </div>
      </button>
    );
  }

  const [actionExpanded, setActionExpanded] = useState(false);
  const [pastExpanded, setPastExpanded] = useState(false);

  return (
    // This tab predates SECTION_SHELL_CLASS and hand-rolls its own root, so it inherited
    // neither the phone padding nor the sideways-scroll backstop that the other 18
    // sections get for free (SCRUM-1405). Matching the shell's max-md: pair here rather
    // than adopting the constant itself, because this tab puts its padding on the sticky
    // header and body instead of the root.
    <div className="h-full min-h-0 overflow-y-auto bg-white max-md:overflow-x-hidden">
      {/* Sticky header keeps the title + Add button visible while the list scrolls */}
      <div className="sticky top-0 z-10 bg-white px-6 pt-6 pb-4 max-md:px-4 max-md:pt-4">
        <SectionTitle
          title="Appointments"
          action={(
            // Full width once SectionTitle has wrapped it, so "Print" and
            // "+ Add Appointment" get the whole line instead of being pushed off the
            // right edge — the reported "+ Add Appointme" truncation (SCRUM-1405).
            <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
              {canSeeDeleted && (
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600 select-none">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  Show deleted
                  {showDeleted && deletedCount > 0 && (
                    <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                      {deletedCount}
                    </span>
                  )}
                </label>
              )}
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Printer className="h-4 w-4" />}
                onClick={handlePrintAppointments}
                disabled={isLoading || rows.length === 0}
              >
                Print
              </Button>
              <Button
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setCreateOpen(true)}
              >
                Add Appointment
              </Button>
            </div>
          )}
        />

        {/* Equal-width columns spanning the full section width. Collapses to
            3 then 2 columns on narrower panes so the labels never truncate. */}
        {!isLoading && !isError && liveRows.length > 0 && (
          // `auto-rows-fr` below `md` equalises the row heights, so the odd fifth tile
          // on its own row matches the four above it instead of collapsing to its own
          // content height (SCRUM-1405, "the stat cards are also uneven").
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 max-md:auto-rows-fr">
            {/* Total is the "clear" tile, so it never shows the selected ring —
                an always-highlighted card at rest would read as a stuck filter. */}
            <ApptStatTile
              label="Total"
              value={liveRows.length}
              hint="Every appointment on file — Appointments + No-Shows + Cancelled. Click to clear the status filter."
              onClick={() => setStatusFilter('')}
            />
            <ApptStatTile
              label="Appointments"
              value={actualCount}
              hint="Real appointments, past and upcoming — excludes cancelled and no-shows. Click to show only these."
              onClick={() => toggleStatusFilter(APPT_BUCKET.appointments)}
              active={statusFilter === APPT_BUCKET.appointments}
            />
            <ApptStatTile
              label="No-Shows"
              value={noShowCount}
              tone="red"
              hint="Missed, not cancelled. Click to show only these."
              onClick={() => toggleStatusFilter(APPT_BUCKET.noShow)}
              active={statusFilter === APPT_BUCKET.noShow}
            />
            <ApptStatTile
              label="Cancelled"
              value={cancelledCount}
              hint="Cancelled before the visit. Click to show only these."
              onClick={() => toggleStatusFilter(APPT_BUCKET.cancelled)}
              active={statusFilter === APPT_BUCKET.cancelled}
            />
            <ApptStatTile
              label="Actions Required"
              value={actionRequiredTotal}
              tone="amber"
              hint="Past appointments not checked out, or with products still to disburse or purchase. A subset of Appointments, so not counted again in Total. Click to show only these."
              onClick={() => toggleStatusFilter(APPT_BUCKET.actionRequired)}
              active={statusFilter === APPT_BUCKET.actionRequired}
            />
          </div>
        )}

        {/* Filters sit directly above the lists, below the tiles, because they
            narrow the lists only — the tiles stay whole-patient totals. */}
        {!isLoading && !isError && allRows.length > 0 && (
          // Right-aligned under the tiles, so the controls line up with the
          // "+ Add Appointment" / "Print" actions above them rather than
          // starting a second left edge. Fixed widths, not `flex-1`: a growing
          // search box would push itself back to the left edge of the row.
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            {filtersActive && (
              <div className="flex items-center gap-2 text-xs text-slate-500 max-md:w-full">
                <span className="tabular-nums">
                  Showing {rows.length} of {allRows.length}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setFacilityFilter('');
                    setStatusFilter('');
                  }}
                  className="font-medium text-blue-600 hover:text-blue-800"
                >
                  Clear
                </button>
              </div>
            )}
            {/* One entry plus "All facilities" means there is nothing to choose
                between, so the selector only appears once it can do something —
                but never disappears while a facility is selected, which would
                strand the list on a filter with no control to undo it. */}
            {(facilityOptions.length > 2 || facilityFilter !== '') && (
              <FilterSelect
                options={facilityOptions}
                value={facilityFilter}
                onChange={setFacilityFilter}
                label="Filter by facility"
                className="w-48 shrink-0 max-md:w-full"
              />
            )}
            {/* The other half of the stat tiles: the same five buckets, plus the
                raw statuses the tiles fold together. Always offered once there
                are live rows — unlike facility, there is always something to
                choose between. */}
            {liveRows.length > 0 && (
              <FilterSelect
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                label="Filter by status"
                className="w-48 shrink-0 max-md:w-full"
              />
            )}
            <div className="relative w-64 shrink-0 max-md:w-full">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search group, type, or title…"
                aria-label="Search appointments by group, type, or title"
                className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content flows in the single scroll container above (matches the other section tabs) */}
      <div className="space-y-6 px-6 pb-6 max-md:px-4">
        {detailError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {detailError}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="py-4 text-sm text-red-600">Failed to load appointments.</p>
        ) : (
          <>
            {/* Upcoming */}
            <section>
              <h3 className="text-sm font-bold text-slate-700 border-b border-slate-200 pb-2 mb-3">
                Upcoming
              </h3>
              {upcoming.length === 0 ? (
                <p className="text-sm text-slate-400 italic">{filtersActive ? 'No upcoming appointments match these filters.' : 'No upcoming appointments.'}</p>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((a) => (
                    <AppointmentCard key={a.appointmentId} appt={a} stats={apptStatsMap.get(a.appointmentId)} />
                  ))}
                </div>
              )}
            </section>

            {/* Past Actions Required */}
            {actionRequired.length > 0 && (
              <section>
                <button
                  type="button"
                  onClick={() => setActionExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 text-sm font-bold text-amber-600 border-b border-slate-200 pb-2 mb-3 cursor-pointer hover:text-amber-700 transition-colors"
                >
                  {actionExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <AlertTriangle className="h-4 w-4" />
                  Past Actions Required ({actionRequired.length})
                </button>
                {actionExpanded && (
                  <div className="space-y-3">
                    {actionRequired.map((a) => (
                      <AppointmentCard
                        key={a.appointmentId}
                        appt={a}
                        stats={apptStatsMap.get(a.appointmentId)}
                        isPast
                        actionReasons={apptActionReasons(a)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Past */}
            <section>
              <h3 className="text-sm font-bold text-slate-700 border-b border-slate-200 pb-2 mb-3">
                Past
              </h3>
              {past.length === 0 ? (
                <p className="text-sm text-slate-400 italic">{filtersActive ? 'No past appointments match these filters.' : 'No past appointments.'}</p>
              ) : (
                <div className="space-y-3">
                  {(pastExpanded ? past : past.slice(0, 10)).map((a) => (
                    <AppointmentCard key={a.appointmentId} appt={a} stats={apptStatsMap.get(a.appointmentId)} isPast />
                  ))}
                  {!pastExpanded && past.length > 10 && (
                    <button
                      type="button"
                      onClick={() => setPastExpanded(true)}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-2 rounded-md hover:bg-blue-50 transition-colors"
                    >
                      Show All ({past.length} total)
                    </button>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <AppointmentCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultPatientId={patientId}
        defaultPatient={patientSummary}
        onSuccess={(appointment) => {
          setCreateOpen(false);
          setDetailAppointment(appointment);
          void refreshAppointments();
        }}
      />

      {detailAppointment && (
        <AppointmentDetailModal
          isOpen={Boolean(detailAppointment)}
          onClose={() => {
            if (suppressCloseRef.current) {
              suppressCloseRef.current = false;
              return;
            }
            setDetailAppointment(null);
          }}
          appointment={detailAppointment}
          onUpdated={(updatedAppointment) => {
            suppressCloseRef.current = true;
            setDetailAppointment(updatedAppointment);
            void refreshAppointments();
          }}
          onDeleted={() => {
            setDetailAppointment(null);
            void refreshAppointments();
          }}
        />
      )}
    </div>
  );
}

// ─── Tasks Tab ────────────────────────────────────────────────────────────────

const TASK_FILTER_TABS = [
  { flTasks: 4, label: 'Past Due',    countKey: 'pastDue'   as const, showCount: true },
  { flTasks: 3, label: 'Due Today',   countKey: 'dueToday'  as const, showCount: true },
  { flTasks: 2, label: 'Upcoming',    countKey: 'upcoming'  as const, showCount: true },
  { flTasks: 5, label: 'Complete',    countKey: 'completed' as const, showCount: false },
  { flTasks: 6, label: 'Inactive',    countKey: 'inactive'  as const, showCount: false },
  { flTasks: 1, label: 'All Entries', countKey: 'all'       as const, showCount: false },
];

/**
 * Who the task is for. Automatic is a task assigned to Prospera, which works
 * through those on its own; the tab opens on Human so the badges count only the
 * team's work. Not saved: it reopens on Human every time.
 */
const TASK_SOURCE_OPTIONS: { value: TaskSource; label: string }[] = [
  { value: 'human', label: 'Human' },
  { value: 'automatic', label: 'Automatic' },
  { value: 'all', label: 'All' },
];

const TASK_STATUS_CLASS: Record<string, string> = {
  'Past Due':  'text-red-600 font-semibold',
  'Due':       'text-orange-600 font-semibold',
  'Upcoming':  'text-blue-600',
  'Completed': 'text-slate-500',
  'Inactive':  'text-slate-400',
};

/**
 * The label comes from the one shared resolver so this tab can never disagree
 * with the badge, the Done gate, or the task modal's read-only lock. The local
 * fallback this replaces read a '0000-00-00' due date as a real date and called
 * those tasks 'Past Due' (and a missing one 'Upcoming'), where the server calls
 * both Inactive — a task with no due date could therefore be offered a Done
 * button here. Only the styling stays local.
 */
function deriveTaskStatus(task: Task): { label: string; className: string } {
  const label = resolveTaskStatusLabel(task);
  return { label, className: TASK_STATUS_CLASS[label] ?? 'text-slate-500' };
}

function priorityLabel(p: number | string | undefined | null): string {
  if (typeof p === 'string') return p;
  if (p === 1) return 'Low';
  if (p === 2) return 'Medium';
  if (p === 3) return 'High';
  return 'Normal';
}

// Exported for tests: mounting it directly avoids the whole patient chart.
export function TasksTab({ patientId, patientSummary }: { patientId: number; patientSummary: PatientSummary | null }) {
  const queryClient = useQueryClient();
  const { hasMinUserLevel, user: authUser } = useAuth();
  const isAdmin = hasMinUserLevel(3);
  const [activeFilter, setActiveFilter] = useState<number | null>(null);
  const [taskSource, setTaskSource] = useState<TaskSource>('human');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // Done opens the same "Complete Task" modal the Tasks list uses, so a
  // completion note can be attached here too instead of silently completing.
  const [completeTarget, setCompleteTarget] = useState<Task | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<string>('lastDue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: savedTab } = useQuery({
    queryKey: ['task-filter-tab'],
    queryFn: getTaskFilterTab,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (activeFilter === null && savedTab != null) {
      setActiveFilter(savedTab);
    }
  }, [savedTab, activeFilter]);

  const handleFilterChange = useCallback((flTasks: number) => {
    setActiveFilter(flTasks);
    setSelectedIds(new Set());
    saveTaskFilterTab(flTasks).catch(() => {});
    queryClient.setQueryData(['task-filter-tab'], flTasks);
  }, [queryClient]);

  const handleTaskSourceChange = useCallback((next: TaskSource) => {
    setTaskSource(next);
    // Same as a sub-tab change: ticked rows from the old list must not carry
    // over into a bulk action on the new one.
    setSelectedIds(new Set());
  }, []);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDir(direction);
  }, []);

  const resolvedFilter = activeFilter ?? 1;

  const { data: tasksPage, isLoading } = useQuery({
    queryKey: ['patient-tasks', patientId, taskSource, resolvedFilter, sortKey, sortDir],
    queryFn: () => listTasks({ patientId, taskSource, flTasks: resolvedFilter, pageSize: 200, sortBy: sortKey, sortDir }),
  });

  // Filtered the same way as the list, so each badge counts the list under it.
  const { data: counts } = useQuery({
    queryKey: ['patient-task-counts', patientId, taskSource],
    queryFn: () => getTaskCounts({ patientId, taskSource }),
  });

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: listUsers,
  });

  /**
   * The ids a bulk action may actually touch. The checkboxes already refuse a
   * closed task (`isRowSelectable` on the table), but a selection outlives the
   * rows it was made from: complete a task in another tab, or let the list
   * refresh under a standing selection, and an id for a now-closed task is
   * still in the set. Re-check against the rows on screen every time.
   * A function declaration, not a const, so it can read `tasksPage` — which is
   * assigned above — at call time from inside the mutations below.
   */
  function actionableIds(ids: number[]): number[] {
    const open = new Set((tasksPage?.data ?? []).filter(isTaskOpen).map((t) => t.planTaskID));
    return ids.filter((id) => open.has(id));
  }

  function invalidateTaskQueries() {
    void queryClient.invalidateQueries({ queryKey: ['patient-tasks', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-task-counts', patientId] });
    // The sidebar's Tasks count and the Dashboard's Pending Tasks badge.
    void queryClient.invalidateQueries({ queryKey: MY_TASK_COUNTS_KEY });
  }

  const bulkCompleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.allSettled(actionableIds(ids).map((id) => completeTask(id)));
    },
    onSettled: () => {
      setSelectedIds(new Set());
      invalidateTaskQueries();
    },
  });

  // De-Activate terminates each task as 'Inactive' (PATCH
  // /tasks/{id}/deactivate): pending=0 with no dateCompleted, no recurrence
  // advance. Legacy — and this call until now — reused the Done endpoint with
  // a canned note, which recorded every de-activation as a completion.
  // The note itself is kept: it is the record of who de-activated the task.
  const bulkDeactivateMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const userFullName = authUser?.displayName?.trim() || 'unknown user';
      const notes = `Manually de-activated by ${userFullName}`;
      await Promise.allSettled(actionableIds(ids).map((id) => deactivateTask(id, { notes })));
    },
    onSettled: () => {
      setSelectedIds(new Set());
      invalidateTaskQueries();
    },
  });

  const tasks = tasksPage?.data ?? [];

  function refreshTasks() {
    invalidateTaskQueries();
  }

  const selectedCount = selectedIds.size;
  const bulkBusy = bulkCompleteMutation.isPending || bulkDeactivateMutation.isPending;
  const bulkDisabled = selectedCount === 0 || bulkBusy;

  const taskColumns = useMemo<DataTableColumn<Task>[]>(() => {
    function renderDate(value: string | null | undefined) {
      // parseDateValue treats a date-only "YYYY-MM-DD" as local midnight, avoiding
      // the UTC-parse off-by-one that rendered task dates a day early.
      const d = parseDateValue(value);
      if (!d) return <span className="text-slate-400">—</span>;
      const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const year = d.toLocaleDateString('en-US', { year: 'numeric' });
      // Two presentations of the same date, picked by CSS rather than a JS branch.
      //
      // The stacked form is deliberate — it buys column width in the desktop table and
      // is not the wrap bug SCRUM-1403 read it as. But in the phone card view there is
      // no column to save, so "Jun 24" over "2026" just looks broken. Rendering both and
      // toggling with `md:hidden` / `hidden md:flex` keeps the desktop cell byte-identical
      // and avoids putting a `useIsBelow` subscriber inside a per-row render function.
      return (
        <>
          <span className="text-xs whitespace-nowrap md:hidden">{monthDay}, {year}</span>
          <span className="hidden leading-tight whitespace-nowrap md:flex md:flex-col md:items-start">
            <span className="text-xs">{monthDay}</span>
            <span className="text-xs text-slate-400">{year}</span>
          </span>
        </>
      );
    }

    return [
      {
        key: 'name',
        header: 'Task',
        sortable: true,
        render: (row) => (
          <span className="text-xs font-medium text-slate-800">{row.name ?? row.title ?? '—'}</span>
        ),
      },
      {
        key: 'planTypeName',
        header: 'Plan',
        render: (row) => {
          if (!row.planID) return <span className="text-xs text-slate-400">—</span>;
          const label = row.planTypeName?.trim() || `Plan #${planNumberOrNull(row.planDisplayID) ?? row.planID}`;
          return (
            <Link
              to={`/plans/${row.planID}`}
              // The row opens the task; the link must only open the plan.
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              {label}
            </Link>
          );
        },
      },
      {
        key: 'derivedStatus',
        header: 'Status',
        render: (row) => {
          const status = deriveTaskStatus(row);
          const badgeVariant: 'error' | 'warning' | 'info' | 'success' | 'neutral' =
            status.label === 'Past Due' ? 'error'
            : status.label === 'Due' ? 'warning'
            : status.label === 'Upcoming' ? 'info'
            : status.label === 'Completed' ? 'success'
            : 'neutral';
          return <Badge variant={badgeVariant} size="sm" className="whitespace-nowrap">{status.label}</Badge>;
        },
      },
      {
        key: 'assignedToName',
        header: 'Assignee',
        render: (row) => {
          if (row.assignedToName?.trim()) {
            return (
              <span className="inline-flex items-center gap-1.5">
                <Avatar src={row.assignedToImage ?? undefined} name={row.assignedToName} size="xs" />
                <span className="text-xs text-slate-700 truncate max-w-[80px]">{row.assignedToName}</span>
              </span>
            );
          }
          if (row.assignedToID) {
            return (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200">
                  <Bot className="h-3 w-3 text-slate-500" />
                </span>
                <span className="text-xs text-slate-500">System</span>
              </span>
            );
          }
          return <span className="text-xs text-slate-400">—</span>;
        },
      },
      {
        key: 'priority',
        header: 'Priority',
        sortable: true,
        render: (row) => (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
            row.priority === 3 || (row.priority as unknown) === 'high'
              ? 'bg-orange-100 text-orange-700'
              : row.priority === 1 || (row.priority as unknown) === 'low'
                ? 'bg-slate-100 text-slate-500'
                : 'bg-blue-100 text-blue-700'
          }`}>
            {priorityLabel(row.priority)}
          </span>
        ),
      },
      {
        key: 'recurring',
        header: 'Occurs',
        render: (row) => <span className="text-xs text-slate-500">{row.recurring ? 'Recurring' : 'Once'}</span>,
      },
      {
        key: 'dateStarted',
        header: 'Added',
        sortable: true,
        render: (row) => renderDate(row.dateStarted),
      },
      {
        key: 'lastDue',
        header: 'Due',
        sortable: true,
        render: (row) => renderDate(row.lastDue ?? row.dueDate ?? null),
      },
      {
        key: 'dateCompleted',
        header: 'Done',
        sortable: true,
        width: 'w-28',
        render: (row) => {
          const status = deriveTaskStatus(row);
          const isComplete = status.label === 'Completed';
          const isInactive = status.label === 'Inactive';
          const taskId = row.planTaskID ?? row.taskItemID ?? 0;
          const doneStr = row.dateCompleted && row.dateCompleted !== '0000-00-00' && row.dateCompleted !== '0000-00-00 00:00:00'
            ? row.dateCompleted : null;
          const showDoneButton = !isComplete && !isInactive && !(status.label === 'Upcoming' && row.required === 1);
          if (isComplete && doneStr) {
            return renderDate(doneStr);
          }
          if (showDoneButton) {
            return (
              <TaskDoneButton
                taskId={taskId}
                onComplete={() => setCompleteTarget(row)}
                onRescheduled={() => invalidateTaskQueries()}
              />
            );
          }
          return null;
        },
      },
    ];
  }, []);

  return (
    // Same story as the Appointments tab: hand-rolled root, no SECTION_SHELL_CLASS,
    // so the phone padding and the overflow backstop have to be added here (SCRUM-1403).
    <div className="flex flex-col h-full max-md:overflow-x-hidden">
      {/*
        The action group wraps onto its own line below `md`. Unwrapped, "Mark Done /
        De-Activate / + Add Task" plus the heading is wider than 375px, and because this
        row is the widest thing in the tab it was what scrolled the whole page sideways —
        which is why the ticket reads as the patient header and tab strip being clipped
        rather than as a button being cut off.
      */}
      <div className="flex items-center justify-between gap-4 flex-shrink-0 px-6 pt-4 max-md:flex-wrap max-md:px-4">
        <h2 className="text-lg font-semibold text-slate-900">Tasks</h2>
        <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => bulkCompleteMutation.mutate(Array.from(selectedIds))}
            disabled={bulkDisabled}
            loading={bulkCompleteMutation.isPending}
          >
            Mark Done
          </Button>
          {isAdmin ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bulkDeactivateMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDisabled}
              loading={bulkDeactivateMutation.isPending}
            >
              De-Activate
            </Button>
          ) : null}
          <Button
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Add Task
          </Button>
        </div>
      </div>

      {/*
        Six filters wrap to three stacked rows on a phone and eat the list. Below `md`
        they scroll in their own row instead, which is the idiom DetailSubNav already
        uses for the section strip — `overflow-x-auto scrollbar-hide` on the row,
        `shrink-0` on the items so they keep their labels rather than compressing.
      */}
      {/*
        The Show control sits at the right-hand end of the same row. Below `md` the
        sub-tabs take the full width and keep their own sideways scroll, and the
        control wraps onto a line of its own rather than scrolling away with them.
      */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-slate-200 px-6 mt-3 pb-1 flex-shrink-0 max-md:px-4">
        <div className="flex flex-wrap items-center gap-1 min-w-0 max-md:w-full max-md:flex-nowrap max-md:overflow-x-auto max-md:scrollbar-hide">
          {TASK_FILTER_TABS.map((tab) => {
            const isActive = resolvedFilter === tab.flTasks;
            const count = counts?.[tab.countKey] ?? 0;
            return (
              <button
                key={tab.flTasks}
                type="button"
                onClick={() => handleFilterChange(tab.flTasks)}
                className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors max-md:shrink-0 max-md:whitespace-nowrap ${
                  isActive
                    ? 'border-b-2 border-primary text-primary bg-primary/10'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab.label}
                {tab.showCount && count > 0 ? (
                  <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none ${
                    isActive ? 'bg-primary text-white' : 'bg-red-500 text-white'
                  }`}>
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div role="group" aria-label="Show" className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-slate-500">Show:</span>
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
            {TASK_SOURCE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleTaskSourceChange(option.value)}
                aria-pressed={taskSource === option.value}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  taskSource === option.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DataTable<Task>
          data={tasks}
          columns={taskColumns}
          keyField="planTaskID"
          loading={isLoading}
          selectable
          // A completed or inactive task cannot be completed or de-activated,
          // so it cannot be ticked either — the bulk bar acts on what is ticked.
          isRowSelectable={isTaskOpen}
          selectedIds={selectedIds as Set<string | number>}
          onSelectionChange={(ids) => setSelectedIds(ids as Set<number>)}
          onRowClick={setSelectedTask}
          manualSort
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          page={1}
          pageSize={tasks.length || 1}
          total={tasks.length}
          onPageChange={() => {}}
          emptyTitle="No tasks found"
          emptyDescription="No tasks match the current filter."
        />
      </div>

      {createOpen && (
        <TaskModal
          onClose={() => setCreateOpen(false)}
          onSaved={refreshTasks}
          users={users}
          initialPatient={patientSummary ?? undefined}
        />
      )}
      {selectedTask && (
        <TaskModal
          item={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onSaved={refreshTasks}
        />
      )}
      {completeTarget && (
        <CompleteModal
          task={completeTarget}
          onClose={() => setCompleteTarget(null)}
          onSaved={invalidateTaskQueries}
        />
      )}
    </div>
  );
}

// ─── Payments Tab ─────────────────────────────────────────────────────────────

/** Rows per page in the patient payments grid. Server cap is 200 (V5Controller::resolveListPagination). */
const PAYMENTS_PAGE_SIZE = 100;

function PaymentsTab({ patientId, patientSummary, patientEmail, patientPhone, patientPhoneCountryCode, patientName }: { patientId: number; patientSummary?: PatientSummary | null; patientEmail: string | null; patientPhone: string | null; patientPhoneCountryCode?: string | null; patientName: string }) {
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuth();
  const userTz = useUserTimezone();
  const canManageGiftCards = (user?.userLevel ?? 0) > 3 || hasPermission(14);
  const [subTab, setSubTab] = useState<'payments' | 'methods'>('payments');

  // SCRUM-1868: a hyperlink elsewhere (e.g. App Orders) can jump straight to
  // one payment via ?paymentId=, the same way CommLogSection's
  // ?commLogId= opens straight to one message. Read once on mount, not on
  // every render — after the modal is closed it must stay closed even though
  // the URL still carries the param; it's a bootstrap hint, not a binding.
  const [searchParams] = useSearchParams();

  const [paymentsPageNum, setPaymentsPageNum] = useState(1);

  // The tab isn't remounted when the route switches to another patient, so the
  // page index has to be reset explicitly or patient B opens on patient A's page.
  useEffect(() => {
    setPaymentsPageNum(1);
  }, [patientId]);

  const { data: paymentsPage, isLoading, isError } = useQuery({
    queryKey: ['patient-payments', patientId, paymentsPageNum],
    queryFn: () => getPayments({ patientId }, { page: paymentsPageNum, pageSize: PAYMENTS_PAGE_SIZE }),
    // Keep the previous page on screen while the next one loads, so paging
    // doesn't flash the table back to a skeleton.
    placeholderData: (prev) => prev,
  });

  // Lifetime totals for THIS patient, computed server-side over every payment
  // row rather than the page on screen. `/payments/stats` honours the same
  // patientId filter as the list (PaymentsController::applyFilters), so the two
  // always describe the same set — the tile just isn't limited to one page.
  const { data: paymentStats } = useQuery({
    queryKey: ['patient-payment-stats', patientId],
    queryFn: () => getPaymentStats({ patientId }),
    staleTime: 60_000,
  });

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60 * 1000,
  });

  const usersById = useMemo(() => {
    const map = new Map<number, AdminUser>();
    users.forEach((user) => map.set(user.userId, user));
    return map;
  }, [users]);

  const [directPaymentLinkOpen, setDirectPaymentLinkOpen] = useState(false);
  const [chargePaymentOpen, setChargePaymentOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [detailPaymentId, setDetailPaymentId] = useState<number | null>(() => {
    const fromUrl = Number(searchParams.get('paymentId'));
    return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : null;
  });

  // Methods / Gift Cards sub-tab state
  const [showDeletedMethods, setShowDeletedMethods] = useState(false);
  const [showUsedGiftCards, setShowUsedGiftCards] = useState(false);
  const [addMethodOpen, setAddMethodOpen] = useState(false);
  const [addMethodMerchantId, setAddMethodMerchantId] = useState<number | null>(null);
  // Add Billing Method capture mode: manual Forward.js card entry vs reading
  // the card on an in-person terminal (tokenize-only, no charge).
  const [addMethodCapture, setAddMethodCapture] = useState<'manual' | 'terminal'>('manual');
  const [addMethodTerminalId, setAddMethodTerminalId] = useState<number | null>(null);
  // Surfaced when a terminal-read card is not eligible to be saved for future
  // use (Forward returns a single-use token). Such cards are never persisted.
  const [addMethodTerminalNotice, setAddMethodTerminalNotice] = useState<string | null>(null);
  const [issueGiftCardOpen, setIssueGiftCardOpen] = useState(false);
  const [giftCardDetailId, setGiftCardDetailId] = useState<number | null>(null);

  const { data: allBillingMethods, isLoading: methodsLoading } = useQuery({
    queryKey: ['patient-billing-methods-manage', patientId],
    queryFn: () => listBillingMethods(patientId, true),
    enabled: subTab === 'methods',
  });

  const visibleMethods = useMemo(() => {
    if (!allBillingMethods) return [];
    const filtered = showDeletedMethods
      ? allBillingMethods
      : allBillingMethods.filter((m) => m.endDate == null);
    return [...filtered].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
  }, [allBillingMethods, showDeletedMethods]);

  const invalidateBillingMethods = () => {
    void queryClient.invalidateQueries({ queryKey: ['patient-billing-methods-manage', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-billing-methods', patientId] });
  };

  const closeAddMethod = () => {
    setAddMethodOpen(false);
    setAddMethodMerchantId(null);
    setAddMethodCapture('manual');
    setAddMethodTerminalId(null);
    setAddMethodTerminalNotice(null);
  };

  // Forward terminals available for the in-person "save card" flow. Loaded
  // only while the Add Billing Method modal is open with terminal capture
  // selected; filtered to the chosen merchant below.
  const { data: addMethodTerminals } = useQuery({
    queryKey: ['payment-terminals'],
    queryFn: () => listPaymentTerminals(),
    enabled: addMethodOpen && addMethodCapture === 'terminal',
    staleTime: 5 * 60_000,
  });

  const addMethodFilteredTerminals = useMemo(
    () => (addMethodTerminals ?? []).filter((t) => {
      if (!addMethodMerchantId) return true;
      return t.accountForwardId === addMethodMerchantId;
    }),
    [addMethodTerminals, addMethodMerchantId],
  );

  const addMethodSelectedTerminal: PaymentTerminal | null = useMemo(
    () => addMethodFilteredTerminals.find((t) => t.terminalForwardId === addMethodTerminalId) ?? null,
    [addMethodFilteredTerminals, addMethodTerminalId],
  );

  // Tokenize-only terminal read result. Reuse-eligible cards are already
  // persisted card-on-file by the backend, so we just refresh + close.
  // Non-eligible cards (single-use tokens) are never saved here — surface a
  // clear notice and keep the modal open instead of implying success.
  const handleAddMethodTerminalResult = (result: TerminalFlowResult) => {
    if (result.mode !== 'tokenize') return;
    // Reuse-eligible AND persisted (no save_warning) → backend stored the
    // card-on-file. Refresh the list and close.
    if (result.data.is_reusable && !result.data.save_warning) {
      invalidateBillingMethods();
      closeAddMethod();
      return;
    }
    // Either not reuse-eligible (single-use token, never saved) or the
    // card-on-file insert failed (save_warning). Surface it and keep the modal
    // open rather than implying the card was saved.
    setAddMethodTerminalNotice(
      result.data.save_warning
        ?? 'This card is not eligible to be saved for future use. It was not added. '
          + 'To take a one-time payment, use the Charge flow instead.',
    );
  };

  const softDeleteMethodMutation = useMutation({
    mutationFn: (id: number) => deleteBillingMethod(id),
    onSuccess: invalidateBillingMethods,
    // e.g. 409 CARD_IN_USE: the card is still charged by an active recurring payment.
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const restoreMethodMutation = useMutation({
    mutationFn: (id: number) => restoreBillingMethod(id),
    onSuccess: invalidateBillingMethods,
  });

  const setDefaultMethodMutation = useMutation({
    mutationFn: (id: number) => setDefaultBillingMethod(id),
    onSuccess: (activeMethods) => {
      const activeMap = new Map(activeMethods.map((m) => [m.billingMethodID, m]));
      queryClient.setQueryData<BillingMethod[]>(
        ['patient-billing-methods-manage', patientId],
        (old) => (old ?? []).map((m) => activeMap.get(m.billingMethodID) ?? { ...m, isDefault: false }),
      );
      void queryClient.invalidateQueries({ queryKey: ['patient-billing-methods', patientId] });
    },
  });

  const { data: patientGiftCards, isLoading: giftCardsLoading } = useQuery({
    queryKey: ['patient-gift-cards', patientId],
    queryFn: () => listPatientGiftCards(patientId),
    enabled: subTab === 'methods',
  });

  const visibleGiftCardCount = useMemo(() => {
    if (!patientGiftCards) return 0;
    if (showUsedGiftCards) return patientGiftCards.length;
    return patientGiftCards.filter((card) => {
      const balance = card.remainingBalance ?? card.currentBalance ?? 0;
      return balance > 0;
    }).length;
  }, [patientGiftCards, showUsedGiftCards]);

  const payments = paymentsPage?.data ?? [];

  // Count of ALL of this patient's payments, not the page length. The list
  // endpoint returns it as meta.total; falling back to the row count only
  // matters before the first response lands.
  const paymentsTotalCount = paymentsPage?.meta?.total ?? payments.length;

  // Lifetime total across every payment, from /payments/stats. `cashCollected`
  // is money actually received: voided and declined rows excluded, gift-card
  // tenders excluded (the card was already counted when it was sold), and
  // refunds netted out via their stored negative curAmount. Deliberately NOT a
  // client-side sum — that could only ever total the page on screen.
  const totalPayments = paymentStats?.cashCollected ?? null;

  // Any payment write moves both the list (every page) and the lifetime totals,
  // including the Total Spend card in the sidebar, which is the same figure.
  const refreshPayments = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-payment-stats', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-sidebar-stats', patientId] });
  }, [queryClient, patientId]);

  const handlePrintPayments = useCallback(async () => {
    // Opened synchronously inside the click handler — a window.open() after an
    // await gets swallowed by the popup blocker.
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write('<p style="font-family:Arial,sans-serif">Preparing payment history…</p>');

    const displayName = patientSummary?.displayName ?? `Patient #${patientId}`;
    const displayId = patientSummary?.displayId;
    const heading = displayId ? `${displayName} (#${displayId})` : displayName;

    // A printed statement must cover every payment, not whichever page happens
    // to be open. `export: true` lifts the 200-row list cap
    // (V5Controller::resolveListPagination).
    let allPayments: Payment[] = payments;
    try {
      const full = await getPayments(
        { patientId },
        { page: 1, pageSize: Math.max(paymentsTotalCount, PAYMENTS_PAGE_SIZE), export: true },
      );
      allPayments = full.data ?? payments;
    } catch {
      // Fall back to the loaded page rather than printing nothing. The footer
      // below states how many rows the sheet covers and flags the shortfall, so
      // a partial statement can't be mistaken for a complete one.
    }
    const isPartial = allPayments.length < paymentsTotalCount;

    const rows = allPayments
      .filter((p) => !(p.void === true || p.void === '1' || (p.void as unknown) === 1))
      .map((p) => {
        const date = formatPaymentDate(p, userTz, { month: 'numeric', day: 'numeric', year: 'numeric' });
        const type = p.strPayMethod && p.strPayType
          ? `${p.strPayType} (${p.strPayMethod})`
          : p.strPayType ?? p.strPayMethod ?? '—';
        const amount = formatCurrency(p.cardChargedAmount ?? p.curAmount);
        const facility = p.facilityName ?? '';
        const materials = p.materialsText ?? '';
        const notes = p.strMemo ?? '';
        const isVoided = p.void === true || p.void === '1' || (p.void as unknown) === 1;
        // `transStatus`, not `transactionStatus`. Payment has no field by the
        // latter name, so this read was always undefined and every row in this
        // export said 'Manually Entered' unless it was voided. transStatus is
        // what PaymentStatusBadge and paymentsShared both use.
        let status: string = p.transStatus ?? 'Manually Entered';
        if (isVoided) status = 'Void';
        return `<tr>
          <td>${date}</td>
          <td>${type}</td>
          <td style="text-align:right">${amount}</td>
          <td>${facility}</td>
          <td>${materials}</td>
          <td>${notes}</td>
          <td>${status}</td>
        </tr>`;
      })
      .join('');

    // Reset the document so the "Preparing…" placeholder above is replaced
    // rather than appended to.
    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Payments – ${heading}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #1e293b; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  .subtitle { font-size: 13px; color: #64748b; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; border-bottom: 2px solid #334155; padding: 6px 8px; font-weight: 600; }
  td { border-bottom: 1px solid #e2e8f0; padding: 5px 8px; }
  td:nth-child(3), th:nth-child(3) { text-align: right; }
  .total { margin-top: 12px; font-size: 14px; font-weight: 600; }
  @media print { body { margin: 0; } }
</style></head><body>
<h1>${heading}</h1>
<div class="subtitle">Payment History – Printed ${new Date().toLocaleDateString('en-US')}</div>
<table>
  <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Facility</th><th>Materials</th><th>Notes</th><th>Status</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="total">Total: ${totalPayments == null ? '—' : formatCurrency(totalPayments)}</div>
<div class="subtitle">${
      isPartial
        ? `Showing ${allPayments.length} of ${paymentsTotalCount} payments — could not load the full history, so the rows above are incomplete. The total covers all ${paymentsTotalCount}.`
        : `${paymentsTotalCount} payment${paymentsTotalCount === 1 ? '' : 's'} on file. Voided payments are excluded from the total.`
    }</div>
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [payments, paymentsTotalCount, totalPayments, patientId, patientSummary, userTz]);

  const patientPaymentColumns = useMemo<DataTableColumn<Payment>[]>(() => [
    {
      key: 'dtmDate',
      header: 'Date',
      width: 'w-16',
      render: (row) => {
        if (!row.entryDate && !row.dtmDate) return <span className="text-slate-400">—</span>;
        // entryDate (when the payment was taken), converted to the viewer's zone;
        // falls back to the dtmDate business date read literally. See
        // formatPaymentDate.
        const monthDay = formatPaymentDate(row, userTz, { month: 'short', day: 'numeric' });
        const year = formatPaymentDate(row, userTz, { year: 'numeric' });
        return (
          <span className="flex flex-col items-start leading-tight whitespace-nowrap">
            <span className="text-xs">{monthDay}</span>
            <span className="text-xs text-slate-400">{year}</span>
          </span>
        );
      },
    },
    {
      key: 'strPayType',
      header: 'Type',
      render: (row) => <span className="capitalize text-xs">{paymentTypeLabel(row)}</span>,
    },
    {
      key: 'curAmount',
      header: 'Amount',
      width: 'w-20',
      // Charged amount, not the raw stored one — see PaymentsPage: curAmount is
      // gross of the gift card on some payments, so it would double-count the
      // card already shown beneath it.
      render: (row) => {
        const giftCardTotal = row.giftCardTotal ?? 0;
        return (
          <span className="flex flex-col">
            <span className="font-medium text-slate-900">
              {formatCurrency(row.cardChargedAmount ?? row.curAmount)}
            </span>
            {giftCardTotal > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-purple-600">
                <Gift className="h-3 w-3 shrink-0" />
                {formatCurrency(giftCardTotal)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'facilityName',
      header: 'Facility',
      render: (row) => <span className="text-xs text-slate-500">{row.facilityName ?? '—'}</span>,
    },
    {
      key: 'materialsText',
      header: 'Materials',
      render: (row) => (
        <span className="text-xs text-slate-500 truncate max-w-[200px] block" title={row.materialsText ?? undefined}>
          {row.materialsText ?? '—'}
        </span>
      ),
    },
    {
      key: 'strMemo',
      header: 'Notes',
      render: (row) => (
        <span className="text-xs text-slate-500 truncate max-w-[160px] block" title={row.strMemo ?? undefined}>
          {row.strMemo ?? '—'}
        </span>
      ),
    },
    {
      key: 'void',
      header: 'Status',
      width: 'w-20',
      render: (row) => {
        const vu = row.voidUserID ? usersById.get(row.voidUserID) : null;
        const voidUserName = vu ? `${vu.firstName} ${vu.lastName}`.trim() : null;
        return <PaymentStatusBadge payment={row} voidDate={row.voidDate} voidUserName={voidUserName} />;
      },
    },
    {
      key: 'userID',
      header: 'Entered By',
      render: (row) => {
        const lookupName = row.userID ? usersById.get(row.userID) : null;
        const fullName = lookupName
          ? `${lookupName.firstName} ${lookupName.lastName}`.trim()
          : row.enteringUserName ?? null;
        if (fullName) {
          return (
            <span className="text-xs text-slate-500">
              {fullName}{row.entryDate ? ` (entered ${formatShortDate(row.entryDate, userTz)})` : ''}
            </span>
          );
        }
        return <span className="text-xs text-slate-500">{row.userID ? `User #${row.userID}` : '—'}</span>;
      },
    },
  ], [userTz, usersById]);

  return (
    <div className="flex flex-col h-full">
      {/* Header: Tabs + Actions */}
      <div className="flex items-center justify-between gap-4 flex-shrink-0 px-6 pt-4">
        <div className="flex items-center border-b border-slate-200 shrink-0">
          <button
            type="button"
            onClick={() => setSubTab('payments')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-base font-bold transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === 'payments'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            Payments
          </button>
          <button
            type="button"
            onClick={() => setSubTab('methods')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-base font-bold transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === 'methods'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <CreditCard className="h-5 w-5" />
            Billing / Gift Cards
          </button>
        </div>

      </div>

      {/* ─── Payments Sub-Tab ─────────────────────────────────────────── */}
      {subTab === 'payments' && (
        <div className="flex flex-col flex-1 min-h-0 mt-3">
          {isError && (
            <p className="flex-shrink-0 px-6 py-3 text-sm text-red-600">Failed to load payments.</p>
          )}

          <div className="flex-shrink-0 flex flex-wrap items-center gap-3 px-6 mb-2">
            {!isLoading && !isError && payments.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                <span className="text-sm font-medium text-slate-600">Total Payments</span>
                <span className="ml-2 text-lg font-semibold text-slate-900">
                  {totalPayments == null ? '…' : formatCurrency(totalPayments)}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Button size="sm" variant="secondary" onClick={() => void handlePrintPayments()}>
                Print
              </Button>
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
                onClick={() => setDirectPaymentLinkOpen(true)}
              >
                Direct payment link
              </Button>
              <Button size="sm" onClick={() => setChargePaymentOpen(true)}>
                Add Payment
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setRecordPaymentOpen(true)}>
                Manual Entry
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <DataTable<Payment>
              data={payments}
              columns={patientPaymentColumns}
              keyField="paymentID"
              tableFixed
              loading={isLoading}
              onRowClick={(row) => setDetailPaymentId(row.paymentID)}
              page={paymentsPageNum}
              pageSize={PAYMENTS_PAGE_SIZE}
              total={paymentsTotalCount}
              onPageChange={setPaymentsPageNum}
              emptyTitle="No payments found"
              emptyDescription="This patient has no payment records yet."
            />
          </div>
        </div>
      )}

      {/* ─── Methods / Gift Cards Sub-Tab ─────────────────────────────── */}
      {subTab === 'methods' && (
        <div className="flex flex-1 flex-col gap-4 overflow-hidden px-6 pt-3 pb-4">
          {/* Section A: Billing Methods */}
          <section className="flex min-h-0 flex-[3] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-shrink-0 items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-700">
                Billing Methods
                {visibleMethods.length > 0 && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{visibleMethods.length}</span>
                )}
              </h3>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                  <Switch checked={showDeletedMethods} onChange={setShowDeletedMethods} size="sm" />
                  Show deleted
                </label>
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setAddMethodOpen(true)}
                >
                  Add Method
                </Button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg">
              {methodsLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : visibleMethods.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No billing methods found.</p>
              ) : (
                <div className="relative space-y-2 pr-1">
                  {setDefaultMethodMutation.isPending && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
                      <Spinner />
                    </div>
                  )}
                  {visibleMethods.map((method: BillingMethod) => {
                    const isDeleted = method.endDate != null;
                    const isExpired = (() => {
                      if (!method.expireDate) return false;
                      const raw = method.expireDate.trim();
                      let month: number;
                      let fullYear: number;

                      if (raw.includes('-')) {
                        const [y, m] = raw.split('-');
                        fullYear = parseInt(y, 10);
                        month = parseInt(m, 10);
                      } else if (raw.includes('/')) {
                        const [m, y] = raw.split('/');
                        month = parseInt(m, 10);
                        const yr = parseInt(y, 10);
                        fullYear = yr < 100 ? 2000 + yr : yr;
                      } else if (raw.length === 4) {
                        month = parseInt(raw.substring(0, 2), 10);
                        fullYear = 2000 + parseInt(raw.substring(2, 4), 10);
                      } else {
                        return false;
                      }

                      if (isNaN(month) || isNaN(fullYear)) return false;
                      const expEnd = new Date(fullYear, month, 0);
                      return expEnd < new Date();
                    })();
                    return (
                      <div
                        key={method.billingMethodID}
                        className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                          isDeleted
                            ? 'border-slate-100 bg-slate-50 opacity-60'
                            : isExpired
                              ? 'border-slate-200 bg-slate-50'
                              : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {!isDeleted && !isExpired ? (
                            <button
                              type="button"
                              onClick={() => setDefaultMethodMutation.mutate(method.billingMethodID)}
                              disabled={setDefaultMethodMutation.isPending}
                              className="flex-shrink-0"
                              title={method.isDefault ? 'Default billing method' : 'Set as default'}
                            >
                              <Star
                                className={`h-4 w-4 ${
                                  method.isDefault
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-300 hover:text-amber-400'
                                }`}
                              />
                            </button>
                          ) : !isDeleted ? (
                            <span className="h-4 w-4 flex-shrink-0" />
                          ) : null}
                          <div>
                            <p className={`text-sm font-medium ${isDeleted ? 'text-slate-400 line-through' : isExpired ? 'text-slate-400' : 'text-slate-900'}`}>
                              {method.title}
                            </p>
                            <p className={`text-xs ${isExpired ? 'text-slate-400' : 'text-slate-500'}`}>
                              {method.lastFour ? `•••• ${method.lastFour}` : 'Stored method'}
                              {method.expireDate ? ` • Expires ${method.expireDate}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isExpired && !isDeleted ? <Badge variant="error" size="sm">Expired</Badge> : null}
                          {method.isDefault && !isDeleted ? <Badge variant="info" size="sm">Default</Badge> : null}
                          {isDeleted ? (
                            <button
                              type="button"
                              onClick={() => restoreMethodMutation.mutate(method.billingMethodID)}
                              disabled={restoreMethodMutation.isPending}
                              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                              title="Restore billing method"
                            >
                              <Undo2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm(`Remove billing method "${method.title}"?`)) return;
                                softDeleteMethodMutation.mutate(method.billingMethodID);
                              }}
                              disabled={softDeleteMethodMutation.isPending}
                              className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                              title="Remove billing method"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Section B: Gift Cards */}
          <section className="flex min-h-0 flex-[2] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-shrink-0 items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-700">
                Gift Cards
                {visibleGiftCardCount > 0 && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{visibleGiftCardCount}</span>
                )}
              </h3>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                  <Switch checked={showUsedGiftCards} onChange={setShowUsedGiftCards} size="sm" />
                  Show used
                </label>
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIssueGiftCardOpen(true)}
                >
                  Add Gift Card
                </Button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg">
              {giftCardsLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : !patientGiftCards || patientGiftCards.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No gift cards found.</p>
              ) : (
                <div className="space-y-2 pr-1">
                  {patientGiftCards.filter((card: GiftCard) => {
                    if (showUsedGiftCards) return true;
                    const balance = card.remainingBalance ?? card.currentBalance ?? 0;
                    return balance > 0;
                  }).sort((a, b) => {
                    const balA = a.remainingBalance ?? a.currentBalance ?? 0;
                    const balB = b.remainingBalance ?? b.currentBalance ?? 0;
                    if (balA > 0 && balB === 0) return -1;
                    if (balA === 0 && balB > 0) return 1;
                    return 0;
                  }).map((card: GiftCard) => {
                    const cardId = card.giftCardId ?? card.paymentsCreditID;
                    const balance = card.remainingBalance ?? card.currentBalance ?? 0;
                    const isUsed = balance === 0;
                    const isRedeemed = isUsed && (card.redeemedAmount ?? 0) > 0;
                    const displayTitle = (() => {
                      const raw = card.title || card.creditTypeName || '';
                      if (!raw) return `Gift Card #${cardId}`;
                      const el = document.createElement('textarea');
                      el.innerHTML = raw;
                      return el.value.replace(/Gift Credit/gi, 'Gift Card');
                    })();
                    return (
                      <button
                        key={cardId}
                        type="button"
                        onClick={() => setGiftCardDetailId(cardId)}
                        className={`flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:shadow-sm ${isUsed ? 'opacity-60' : ''}`}
                      >
                        <div>
                          <p className={`text-sm font-medium ${isUsed ? 'text-slate-400' : 'text-slate-900'}`}>
                            {displayTitle}
                          </p>
                          <p className={`text-xs ${isUsed ? 'text-slate-300' : 'text-slate-500'}`}>
                            Serial: {card.serialNumber || '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {isUsed && (
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              isRedeemed
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {isRedeemed ? 'Redeemed' : 'Voided'}
                            </span>
                          )}
                          <div className="text-right">
                            <p className={`text-xs ${isUsed ? 'text-slate-300' : 'text-slate-500'}`}>
                              {formatCurrency(card.totalValue ?? 0)} total
                            </p>
                            <p className={`text-sm font-bold ${isUsed ? 'text-slate-400' : 'text-slate-900'}`}>
                              {formatCurrency(balance)} <span className="text-xs font-normal text-slate-500">remaining</span>
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* ─── Modals ───────────────────────────────────────────────────── */}
      <DirectPaymentLinkModal
        patientId={patientId}
        patientEmail={patientEmail}
        patientPhone={patientPhone}
        patientPhoneCountryCode={patientPhoneCountryCode}
        patientName={patientName}
        open={directPaymentLinkOpen}
        onClose={() => setDirectPaymentLinkOpen(false)}
      />

      <ChargePaymentModal
        patientId={patientId}
        open={chargePaymentOpen}
        onClose={() => setChargePaymentOpen(false)}
        onSaved={refreshPayments}
      />

      <RecordPatientPaymentModal
        patientId={patientId}
        open={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        onSaved={refreshPayments}
      />

      <RecordPatientPaymentModal
        patientId={patientId}
        paymentId={detailPaymentId}
        open={!!detailPaymentId}
        onClose={() => setDetailPaymentId(null)}
        onSaved={refreshPayments}
      />

      <IssueGiftCardModal
        open={issueGiftCardOpen}
        onClose={() => setIssueGiftCardOpen(false)}
        onIssued={() => {
          void queryClient.invalidateQueries({ queryKey: ['patient-gift-cards', patientId] });
          setIssueGiftCardOpen(false);
        }}
        defaultPurchaser={patientSummary}
        lockPurchaser
      />

      <Modal
        isOpen={addMethodOpen}
        onClose={closeAddMethod}
        title="Add Billing Method"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Merchant Account</label>
            <MerchantSelector
              value={addMethodMerchantId}
              onChange={(id) => {
                setAddMethodMerchantId(id);
                // Dropping the merchant invalidates the chosen terminal (which
                // is merchant-scoped) and any stale not-eligible notice.
                setAddMethodTerminalId(null);
                setAddMethodTerminalNotice(null);
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Capture Method</label>
            <div className="inline-flex rounded-md border border-slate-300 p-0.5">
              <button
                type="button"
                onClick={() => { setAddMethodCapture('manual'); setAddMethodTerminalNotice(null); }}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  addMethodCapture === 'manual'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Enter card manually
              </button>
              <button
                type="button"
                onClick={() => { setAddMethodCapture('terminal'); setAddMethodTerminalNotice(null); }}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  addMethodCapture === 'terminal'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                In-person terminal
              </button>
            </div>
          </div>

          {addMethodCapture === 'manual' ? (
            <ForwardBillingMethodCapture
              active={addMethodOpen && addMethodCapture === 'manual'}
              patientId={patientId}
              merchantId={addMethodMerchantId}
              onSaved={() => {
                invalidateBillingMethods();
                closeAddMethod();
              }}
            />
          ) : (
            <div className="space-y-3">
              {!addMethodMerchantId ? (
                <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Select a merchant account first to see its terminals.
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Terminal</label>
                  <select
                    value={addMethodTerminalId != null ? `terminal|${addMethodTerminalId}` : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setAddMethodTerminalNotice(null);
                      setAddMethodTerminalId(
                        raw.startsWith('terminal|') ? Number(raw.slice('terminal|'.length)) : null,
                      );
                    }}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select a terminal…</option>
                    {addMethodFilteredTerminals.map((terminal) => (
                      <option
                        key={`terminal-${terminal.terminalForwardId}`}
                        value={`terminal|${terminal.terminalForwardId}`}
                      >
                        {terminal.title}
                        {terminal.hsn ? ` (${terminal.hsn})` : ''}
                      </option>
                    ))}
                  </select>
                  {addMethodFilteredTerminals.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-500">No terminals found for this merchant.</p>
                  ) : null}
                </div>
              )}

              {addMethodTerminalNotice ? (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {addMethodTerminalNotice}
                </div>
              ) : null}

              {addMethodSelectedTerminal ? (
                <TerminalCheckoutPanel
                  terminal={addMethodSelectedTerminal}
                  patientId={patientId}
                  amount={0}
                  merchantId={addMethodMerchantId}
                  mode="tokenize"
                  onSuccess={handleAddMethodTerminalResult}
                  onReset={() => setAddMethodTerminalNotice(null)}
                />
              ) : null}
            </div>
          )}
        </div>
      </Modal>

      {giftCardDetailId !== null && (
        <GiftCardDetailModal
          open={giftCardDetailId !== null}
          onClose={() => setGiftCardDetailId(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['patient-gift-cards', patientId] });
          }}
          giftCardId={giftCardDetailId}
          readOnly={!canManageGiftCards}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PatientDetailPage() {
  const { id, planId: planIdParam } = useParams<{ id: string; planId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const patientId = Number(id);
  const initialPlanId = planIdParam ? Number(planIdParam) : undefined;
  const visitPatient = usePatientHistoryStore((s) => s.visit);
  const [activeSection, setActiveSection] = useState(() =>
    planIdParam
      ? 'plans'
      : resolveInitialPatientSection(searchParams.get('section')),
  );
  // When a lab PDF is opened from the Labs > Documents tab it takes over the
  // right sidebar (widened) in place of the patient details.
  const [labPreview, setLabPreview] = useState<LabPreviewTarget | null>(null);
  // When "Expand" is clicked on a lab detail sidebar, this holds the detail
  // target and is rendered as a full-screen modal at the page root.
  const [labDetailModal, setLabDetailModal] = useState<LabDetailTarget | null>(null);
  // Bumped each time a preview is opened so the panel auto-expands even if
  // the user collapsed it while a previous PDF was showing.
  const [labPreviewExpandTrigger, setLabPreviewExpandTrigger] = useState(0);
  const handleLabPreview = (target: LabPreviewTarget) => {
    setLabPreview(target);
    setLabPreviewExpandTrigger((n) => n + 1);
  };

  // Drop the lab preview when navigating to a different patient.
  useEffect(() => {
    setLabPreview(null);
    setLabDetailModal(null);
  }, [patientId]);

  const { hasPermission: hasPerm, user: sectionUser, accountId: sectionAccountId } = useAuth();
  const hasQaAccess = hasPerm(1) || hasPerm(24);
  const userLevel = sectionUser?.userLevel;
  // Legacy lists Quotes only inside the appointments add-on block
  // (api/v4.2/navigation-patient.php:77). Fail-closed: the tab stays hidden
  // until the practice's add-on row positively says it is on. The column is a
  // tinyint and arrives as a number or a numeric string.
  const { data: accountAddons } = useQuery({
    queryKey: ['account', 'addons', sectionAccountId],
    queryFn: adminApi.getAccountAddons,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const appointmentsAddonEnabled = Number(accountAddons?.appointments ?? 0) !== 0;
  // Vitamins (Vitaboom) is an opt-in module — hide the tab unless the account
  // has it enabled (default-OFF; gated in Prospera-Corp-App). Fail-closed: the
  // tab stays hidden until the status query positively says enabled.
  const { data: vitaminsEnabled = false } = useQuery({
    queryKey: ['vitaboom', 'enabled'],
    queryFn: getVitaboomEnabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Every module-backed tab (SECTION_MODULE_KEYS) follows the per-account
  // module toggle. Fail-open like the sidebar: the module system is
  // default-enabled, so only a positive "disabled" hides a tab. A restricted
  // (peptides-only) account sees exactly Details + Payments + Peptides.
  const { data: accountModulesData } = useAccountModules();
  // Health Nexus is opt-in (SCRUM-1450). `explicit` is the fail-CLOSED signal —
  // false while loading, on error, from the persisted authStore seed alone, and
  // for an account with no rows — so the tab appears only once a fresh
  // /account/modules response has positively reported the module active.
  const { explicit: healthNexusEnabled } = useModuleAccess(HEALTH_NEXUS_MODULE_KEY);
  // Surgery is opt-in AND permission-gated. `explicit` is the fail-CLOSED
  // module signal (false while loading, on error, and for an account with no
  // rows); legacy permission 6 is the separate per-user grant that decided
  // whether the old nav entry rendered at all. Both must hold — the module
  // says the practice has Surgery, the permission says this user may open it.
  const { explicit: surgeryModuleActive } = useModuleAccess(SURGERY_MODULE_KEY);
  const surgeryEnabled = surgeryModuleActive && hasPerm(PERMISSION.SURGERY);
  // Quotes is opt-in for the same reason and reads the same fail-CLOSED signal:
  // the V5 Quotes tab runs alongside the legacy one, so a practice sees it only
  // once someone switches the module on.
  const { explicit: quotesEnabled } = useModuleAccess(QUOTES_MODULE_KEY);
  // `explicit`, not `enabled`: Post-ops is opt-in, so an account with no
  // module rows must stay hidden rather than falling open.
  const { explicit: postOpsModuleActive } = useModuleAccess(POST_OPS_MODULE_KEY);
  const postOpsEnabled = postOpsModuleActive && hasPerm(PERMISSION.POST_OPS);
  const disabledModuleKeys = useMemo(() => {
    const s = new Set<string>();
    for (const m of accountModulesData?.modules ?? []) if (!m.enabled) s.add(m.key);
    return s;
  }, [accountModulesData]);
  // Sections unchecked under the always-on `Patients` module. This is a SECOND,
  // independent gate that overlaps the module gate above for the Memberships
  // tab — the `memberships` module says whether the practice has memberships,
  // this says whether it wants the tab — and either one hides it.
  const disabledPatientsSections = useMemo(
    () => new Set(
      (accountModulesData?.modules ?? []).find((m) => m.key === PATIENTS_MODULE_KEY)?.disabledSections ?? [],
    ),
    [accountModulesData],
  );
  const visibleSections = useMemo(
    () => computeVisibleSections({
      hasQaAccess, vitaminsEnabled, healthNexusEnabled, surgeryEnabled, quotesEnabled, postOpsEnabled,
      userLevel, appointmentsAddonEnabled,
      disabledModuleKeys, disabledPatientsSections,
    }),
    [
      hasQaAccess, vitaminsEnabled, healthNexusEnabled, surgeryEnabled, quotesEnabled, postOpsEnabled,
      userLevel, appointmentsAddonEnabled,
      disabledModuleKeys, disabledPatientsSections,
    ],
  );

  // Deep-link guard: ?section= pointing at a hidden tab renders the default
  // section instead (generalizes the Vitaboom per-case guard to all tabs).
  const effectiveSection = useMemo(
    () => resolveEffectiveSection(visibleSections, activeSection),
    [visibleSections, activeSection],
  );

  const handleSectionChange = (next: string) => {
    if (next !== 'labs') { setLabPreview(null); setLabDetailModal(null); }
    setActiveSection(next);
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      if (next === DEFAULT_SECTION) {
        updated.delete('section');
      } else {
        updated.set('section', next);
      }
      if (next !== 'commlog') updated.delete('commLogId');
      return updated;
    }, { replace: true });
  };

  useEffect(() => {
    const section = searchParams.get('section');
    if (section && SUB_SECTIONS.some((s) => s.key === section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // A link straight to one order (linkedOrder.ts): the section's orders table
  // opens it, then the link leaves the URL so a refresh does not reopen it.
  const linkedOrder = useMemo(() => parseLinkedOrder(searchParams), [searchParams]);
  const clearLinkedOrder = useCallback(() => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.delete(ORDER_PARAM);
      updated.delete(ORDER_VENDOR_PARAM);
      return updated;
    }, { replace: true });
  }, [setSearchParams]);

  const updateAIContext = usePageContext('Patient Detail');
  const setAISidebarContext = useAISidebarStore((s) => s.setContext);

  // Keep the AI context aligned with what's on screen without forcing the panel
  // open. On /patients/:id/plans/:planId the plan is the more specific subject,
  // so focus that instead of the patient.
  useEffect(() => {
    if (initialPlanId !== undefined && Number.isFinite(initialPlanId) && initialPlanId > 0) {
      setAISidebarContext('plan', initialPlanId);
      return;
    }
    if (Number.isFinite(patientId) && patientId > 0) {
      setAISidebarContext('patient', patientId);
    }
  }, [patientId, initialPlanId, setAISidebarContext]);

  useEffect(() => {
    if (activeSection === 'plans' && Number.isFinite(patientId) && patientId > 0) {
      void queryClient.prefetchQuery({
        queryKey: ['patient-plans', patientId],
        queryFn: () => getPlansByPatient(patientId),
        staleTime: 30_000,
      });
    }
  }, [activeSection, patientId, queryClient]);

  const { detail, loading, failed: patientLoadFailed } = usePatientDetail(Number.isFinite(patientId) && patientId > 0 ? String(patientId) : null);
  const patient = detail.patient as Record<string, string | null | undefined> | null;
  const patientPhotoUrl = usePatientPhoto(
    patient && Number.isFinite(patientId) && patientId > 0 ? patientId : null,
    patient?.photoUrl,
  );

  useEffect(() => {
    if (!patient || !patientId) return;

    visitPatient({
      patientId,
      name: `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || `Patient #${patientId}`,
      photoUrl: patient.photoUrl,
    });
  }, [patient, patientId, visitPatient]);

  useEffect(() => {
    if (!patient) return;
    const name = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || `Patient #${patientId}`;
    updateAIContext({ dataSummary: `Viewing patient: ${name} (ID ${patientId})` });
  }, [patient, patientId, updateAIContext]);

  // Whether to render the error card instead of the page. Evaluated here, acted on
  // immediately before the main `return` — every hook this component owns has to run on
  // every render, and this guard used to sit above eight of them (three useMemo, two
  // context hooks, a useQuery and two useState). A bad id therefore rendered eight fewer
  // hooks, and because both `patients/:id` routes render the same element position
  // (App.tsx) React Router reuses the instance across an id change, so navigating from a
  // bad id to a good one threw "Rendered more hooks than during the previous render".
  // Nothing below reads `patientId` in a way NaN breaks: the queries are all `enabled`-
  // gated on a finite id, and the rest is optional-chained off `patient`.
  const invalidPatientId = !patientId || isNaN(patientId);

  const patientName = useMemo(() => {
    if (!patient) return `Patient #${patientId}`;
    const fullName = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim();
    return fullName || `Patient #${patientId}`;
  }, [patient, patientId]);
  const appointmentPatientSummary = useMemo<PatientSummary | null>(() => {
    if (!patientId || !patient) {
      return null;
    }

    const firstName = typeof patient.firstName === 'string' ? patient.firstName : '';
    const lastName = typeof patient.lastName === 'string' ? patient.lastName : '';
    const displayName = `${firstName} ${lastName}`.trim() || `Patient #${patientId}`;
    const birthDate =
      typeof patient.birthDate === 'string'
        ? patient.birthDate
        : typeof patient.dateOfBirth === 'string'
          ? patient.dateOfBirth
          : null;

    return {
      patientId,
      accountId: 0,
      firstName,
      lastName,
      displayName,
      displayId: patient.displayID != null ? String(patient.displayID) : null,
      dateOfBirth: birthDate,
      phone: typeof patient.phone === 'string' ? patient.phone : null,
      homePhone: typeof patient.homePhone === 'string' ? patient.homePhone : null,
      cellPhone: typeof patient.cellPhone === 'string' ? patient.cellPhone : null,
      email: typeof patient.email === 'string' ? patient.email : null,
      photoUrl: typeof patient.photoUrl === 'string' ? patient.photoUrl : null,
      status: 'active',
      activePlanId: null,
      activePlanStage: null,
    };
  }, [patient, patientId]);

  const patientPhone = useMemo(
    () => patient?.cellPhone ?? patient?.phone ?? patient?.homePhone ?? null,
    [patient],
  );
  // The dial code of whichever phone was picked above (the cell's, else the
  // home phone's), so displays format it by its own code.
  const patientPhoneCountryCode =
    patient?.cellPhone != null ? patient.cellCountryCode : patient?.phoneCountryCode;
  const patientEmail = patient?.email ?? null;
  const photoUrl = patientPhotoUrl;

  const voiceCall = useVoiceCall({ autoInit: false });
  const videoCall = useVideoCall();

  const { data: giFormData } = useQuery({
    queryKey: ['patient-forms-list', patientId, false],
    queryFn: () => listPatientForms(patientId, false),
    enabled: Number.isFinite(patientId) && patientId > 0,
    staleTime: 30_000,
  });
  const giFormConfigured = giFormData?.giFormConfigured ?? false;
  const giFormUpToDate = giFormData?.giFormUpToDate ?? false;
  const currentGiFormId = giFormData?.currentGiFormId ?? null;
  const [giEFormsOpen, setGiEFormsOpen] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);

  function renderSection() {
    switch (effectiveSection) {
      case 'demographics':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PatientInfoDemographicsTab patientId={patientId} onSectionNavigate={handleSectionChange} />
          </div>
        );
      case 'health':
        return (
          // Not SECTION_SHELL_CLASS: this tab's header is sticky, and the shell's p-6 is
          // part of the scrollable content, so a sticky child pins below it and leaves a
          // 24px band for cards to scroll through. Same reason Appointments hand-rolls
          // its root — the padding lives on the tab's own header and body instead.
          <div className="h-full min-h-0 overflow-y-auto bg-white max-md:overflow-x-hidden">
            <HealthHistoryTab patientId={patientId} />
          </div>
        );
      case 'plans':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PlansTab
              patientId={patientId}
              initialPlanId={initialPlanId}
              // A plan's Quotes and Payments cards follow the patient's own
              // Quotes and Payments tabs, so the gate lives in one place
              // (computeVisibleSections) and a card never asks for quotes
              // the account cannot open.
              showQuotes={visibleSections.some((s) => s.key === 'quotes')}
              showPayments={visibleSections.some((s) => s.key === 'payments')}
              patientName={patientName}
              patientEmail={patientEmail}
              patientPhone={patientPhone}
            />
          </div>
        );
      case 'surgery':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <SurgeryTab patientId={patientId} planId={initialPlanId} onOpenSection={handleSectionChange} />
          </div>
        );
      case 'appointments':
        return (
          <div className="h-full min-h-0 bg-white max-md:overflow-x-hidden">
            <AppointmentsTab patientId={patientId} patientSummary={appointmentPatientSummary} />
          </div>
        );
      case 'tasks':
        return (
          <div className="h-full min-h-0 flex flex-col bg-white max-md:overflow-x-hidden">
            <TasksTab patientId={patientId} patientSummary={appointmentPatientSummary} />
          </div>
        );
      case 'payments':
        return (
          <div className="h-full min-h-0 flex flex-col bg-white max-md:overflow-x-hidden">
            <PaymentsTab patientId={patientId} patientSummary={appointmentPatientSummary} patientEmail={patientEmail} patientPhone={patientPhone} patientPhoneCountryCode={patientPhoneCountryCode} patientName={patientName} />
          </div>
        );
      case 'memberships':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PatientMembershipsPanel patientId={patientId} />
          </div>
        );
      case 'packages':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PatientPackagesPanel patientId={patientId} patientName={patientName} />
          </div>
        );
      case 'quotes':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PatientQuotesPanel patientId={patientId} patientName={patientName} />
          </div>
        );
      case 'documents':
        return (
          <div className="h-full min-h-0 flex flex-col bg-white max-md:overflow-x-hidden">
            <DocumentsTabFull patientId={patientId} />
          </div>
        );
      case 'notes':
        return (
          <div className="h-full min-h-0 overflow-hidden bg-white max-md:overflow-x-hidden">
            <NotesSection patientId={String(patientId)} patientSummary={appointmentPatientSummary} />
          </div>
        );
      case 'commlog':
        return (
          <div className="h-full min-h-0 overflow-hidden bg-white max-md:overflow-x-hidden">
            <CommLogSection
              patientId={String(patientId)}
              patientName={patientName}
              patientPhone={patientPhone ?? undefined}
              patientEmail={patientEmail ?? undefined}
              patientPhoto={photoUrl}
              smsOptIn={String((patient as Record<string, unknown> | null)?.smsOptIn ?? '1') !== '0'}
              voiceCall={voiceCall}
              videoCall={videoCall}
              highlightMessageId={searchParams.get('commLogId') || undefined}
              readSource="patient_chart"
            />
          </div>
        );
      case 'financing':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <FinancingTab patientId={patientId} />
          </div>
        );
      case 'labs':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <LabsTab
              patientId={patientId}
              onPreview={handleLabPreview}
              linkedOrder={linkedOrder}
              onLinkedOrderOpened={clearLinkedOrder}
            />
          </div>
        );
      case 'imaging':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <LabCategoryDocumentsTab
              patientId={patientId}
              title="Scans/Other"
              fileType="IMAGING"
              documentsFetcher={getPatientImagingDocuments}
              documentsQueryKey="patient-imaging-documents"
              emptyMessage="No scans or other documents found."
              onPreview={handleLabPreview}
            />
            {/* BodySpec is a scan, not a lab (SCRUM-2217): under the section's
                own title and documents. Renders nothing without the module. */}
            <BodySpecScansSection patientId={patientId} linkedOrder={linkedOrder} onLinkedOrderOpened={clearLinkedOrder} />
          </div>
        );
      case 'genetics':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <LabCategoryDocumentsTab
              patientId={patientId}
              title="Genetics"
              fileType="GENETICS"
              documentsFetcher={getPatientGeneticsDocuments}
              documentsQueryKey="patient-genetics-documents"
              emptyMessage="No genetics documents found."
              onPreview={handleLabPreview}
            />
          </div>
        );
      case 'partell':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PartellTab patientId={patientId} linkedOrder={linkedOrder} onLinkedOrderOpened={clearLinkedOrder} />
          </div>
        );
      case 'peptides':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PeptidesTab patientId={patientId} linkedOrder={linkedOrder} onLinkedOrderOpened={clearLinkedOrder} />
          </div>
        );
      case 'vitamins':
        // Opt-in module — a disabled account reaching ?section=vitamins directly
        // gets the placeholder, never the embed (the token broker would 403 anyway).
        if (!vitaminsEnabled) return <PlaceholderSection sectionName="Vitaboom" />;
        return (
          // Not SECTION_SHELL_CLASS: the tab's title + catalog search/filter bar is
          // sticky, and the shell's p-6 is part of the scrollable content, so a sticky
          // child would pin below it and leave a 24px band (same reason as 'health' and
          // Appointments — the padding lives on the tab's own header and body).
          <div className="h-full min-h-0 overflow-y-auto bg-white max-md:overflow-x-hidden">
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-16 text-slate-500">
                  <Spinner size="md" />
                  <span className="ml-3 text-sm">Loading vitamins…</span>
                </div>
              }
            >
              <VitaminsTab patientId={patientId} patient={patient} patientUnavailable={patientLoadFailed} />
            </Suspense>
          </div>
        );
      case 'qa':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <QaTab patientId={patientId} />
          </div>
        );
      case 'post-ops':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PostOpsTab patientId={patientId} planId={initialPlanId} />
          </div>
        );
      case 'items':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <ItemsTab patientId={patientId} />
          </div>
        );
      case 'referrals':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <ReferralsTab patientId={patientId} />
          </div>
        );
      case 'healthnexus':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <HealthNexusSection patientId={patientId} />
          </div>
        );
      case 'files':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PatientFilesTab patientId={patientId} />
          </div>
        );
      case 'forms':
        return (
          <div className="h-full min-h-0 flex flex-col bg-white max-md:overflow-x-hidden">
            <FormsTab patientId={patientId} />
          </div>
        );
      case 'medications':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <MedicationsTab patientId={patientId} />
          </div>
        );
      case 'prescriptions':
        return (
          <div className={SECTION_SHELL_CLASS}>
            <PrescriptionsTab patientId={patientId} />
          </div>
        );
      default: {
        const section = SUB_SECTIONS.find((item) => item.key === effectiveSection);
        return (
          <PlaceholderSection sectionName={section?.label ?? effectiveSection} />
        );
      }
    }
  }

  if (invalidPatientId) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Invalid patient ID.
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden bg-white">
      <div className="h-full min-h-0 overflow-hidden bg-white">
        <DetailPageLayout
          loading={loading && !patient}
          /*
           * Bumped only by handleLabPreview, so picking a lab result still throws the
           * panel open on a phone while changing section does not. Section changes swap
           * `rightPanel` between null (demographics) and a node, which the layout used
           * to read as intent — that was SCRUM-1384.
           */
          panelOpenSignal={labPreviewExpandTrigger}
          subNav={(
            <DetailSubNav
              sections={visibleSections}
              activeSection={effectiveSection}
              onSectionChange={handleSectionChange}
              header={(
                <div className="flex items-center gap-2">
                  <Avatar
                    src={photoUrl}
                    name={patientName}
                    size="sm"
                  />
                  <div className="flex flex-col min-w-0 group-data-[collapsible=icon]:hidden">
                    <div className="flex items-center gap-2 min-w-0">
                      <h2 className="truncate text-sm font-semibold text-slate-900">
                        {patientName}
                      </h2>
                      {Boolean(patient?.isDeleted) && (
                        <Badge variant="error" size="sm">Deleted</Badge>
                      )}
                    </div>
                    {patient?.displayID != null && String(patient.displayID) !== '' && (
                      <span className="truncate text-xs text-slate-500">#{patient.displayID}</span>
                    )}
                  </div>
                </div>
              )}
            />
          )}
          rightPanel={
            effectiveSection === 'demographics' ? null :
            (isLabLikeSection(effectiveSection) && labPreview && isLabDetailTarget(labPreview)) ? (
              <DetailRightPanel key="lab-detail" width="w-[clamp(28rem,50vw,52rem)]" expandTrigger={labPreviewExpandTrigger}>
                <LabDetailPanel
                  target={labPreview}
                  onExpand={() => setLabDetailModal(labPreview)}
                  onClose={() => setLabPreview(null)}
                />
              </DetailRightPanel>
            ) :
            (isLabLikeSection(effectiveSection) && labPreview) ? (
              <DetailRightPanel key="lab-preview" width="w-[clamp(28rem,50vw,52rem)]" expandTrigger={labPreviewExpandTrigger}>
                <LabPdfPreviewPanel
                  patientId={patientId}
                  target={labPreview}
                  onClose={() => setLabPreview(null)}
                  onNewTab={() => setLabPreview(null)}
                />
              </DetailRightPanel>
            ) : (
              <DetailRightPanel key="patient-detail" defaultCollapsed>
                <DetailProfile detail={detail} loading={loading} onSectionNavigate={handleSectionChange} />
              </DetailRightPanel>
            )
          }
        >
          <div className="flex h-full min-h-0 flex-col">
            {giFormConfigured && !giFormUpToDate && effectiveSection !== 'forms' && (
              // flex-wrap lets the button pair drop to its own line on a phone instead of
              // being pushed off the right edge; it never triggers at a width where the row
              // already fits, so md+ renders exactly as before.
              <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-800 max-md:px-4">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                  <span>This patient has not completed the current General Intake form.</span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setInvestigateOpen(true)}
                    className="inline-flex h-7 items-center rounded-md border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                  >
                    Already filled out?
                  </button>
                  <button
                    type="button"
                    onClick={() => setGiEFormsOpen(true)}
                    className="inline-flex h-7 items-center rounded-md bg-amber-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                  >
                    Send Now
                  </button>
                </div>
              </div>
            )}
            <div className="min-h-0 flex-1">{renderSection()}</div>
          </div>
        </DetailPageLayout>
      </div>

      {currentGiFormId && (
        <GiFormSendModal
          patientId={patientId}
          surveyId={currentGiFormId}
          open={giEFormsOpen}
          onClose={() => setGiEFormsOpen(false)}
        />
      )}

      <InvestigateFormsModal
        patientId={patientId}
        open={investigateOpen}
        onClose={() => setInvestigateOpen(false)}
      />

      {labDetailModal?.kind === 'quest-detail' && (
        <QuestLabDetailModal
          open
          onClose={() => setLabDetailModal(null)}
          labResultId={labDetailModal.labResultId}
          orderId={labDetailModal.orderId}
        />
      )}
      {labDetailModal?.kind === 'vibrant-detail' && (
        <VibrantLabDetailModal
          open
          onClose={() => setLabDetailModal(null)}
          labResultId={labDetailModal.labResultId}
          source="vibrant"
        />
      )}
      {labDetailModal?.kind === 'vibrant-order-detail' && (
        <VibrantLabDetailModal
          open
          onClose={() => setLabDetailModal(null)}
          labResultId={labDetailModal.orderId}
          source="vibrant-order"
        />
      )}
      {labDetailModal?.kind === 'access-detail' && (
        <AccessLabDetailModal
          open
          onClose={() => setLabDetailModal(null)}
          labResultId={labDetailModal.labResultId}
        />
      )}
    </div>
  );
}

/** True when a lab preview target is a structured detail (not a PDF preview). */
function isLabDetailTarget(target: LabPreviewTarget): target is LabDetailTarget {
  return (
    target.kind === 'quest-detail' ||
    target.kind === 'vibrant-detail' ||
    target.kind === 'vibrant-order-detail' ||
    target.kind === 'access-detail'
  );
}
